import { GENERIC_CREATION_RETIRED } from '../creationAdmission.js';
import { canonicalDigest } from '@djgg/gameplay-kit';
import { compositionMediaExecutionDigest, compositionReceiptItemsV1, validateAcceptedCompositionV1, type AcceptedCompositionV1 } from '../spec/gameSpec.js';
import { compositionBundleSlotsV1, compositionMediaSlotKindV1 } from '../spec/compositionPresentation.js';
import { compositionInheritedMediaV1, compositionVariantMatchesRefV1, deriveCompositionLineageV1, deriveCompositionRevisionV1, isCompositionSuccessorV1, validateCompositionLineageV1,
  type CompositionMediaRefV1, type CompositionRevisionV1 } from '../compositionLineage.js';
import { compositionMediaOriginV1 } from '../spec/compositionMediaDesign.js';
import { assertCompositionAdmissionFenceV1, assertLockedCompositionAdmissionFenceV1,
  lockCompositionAdmissionControlV1, type CompositionAdmissionFenceV1 } from '../compositionControls.js';
import { isDurableMeasuredRevision } from '../buildPipelines.js';
import { WorkshopRepo } from './workshop.js';
import { workshopDoc, type WorkshopState } from '../workshop.js';
/**
 * CreationsRepo — typed repository over {@link DocStorePort} for
 * `creations/{creationId}` and its slug claim doc.
 *
 * Slug uniqueness follows the CLAIM-DOC pattern (mirrors @djgg/identity's
 * nickname claims): a creation reserves its slug by creating `slugs/{slug}`
 * atomically with the creation doc in one {@link DocStorePort.batch}, guarded by
 * a prior existence read. The single-threaded doc store + the rules layer
 * (create-if-absent on `slugs/{slug}`) make this safe; the batch keeps the two
 * writes all-or-nothing so a creation never exists without its slug claim.
 */
import {
  PlatformError,
  type DocStorePort,
  type DocTxRunner,
  type QuerySpec,
  type Uid,
} from '@djgg/core';
import {
  actionLogDoc,
  buildPipelinesDoc,
  buildJobDoc,
  creationDoc,
  compositionIntentProposalHeadDocV2,
  creationsCollection,
  likesCollection,
  slugDoc,
  versionDoc,
  versionsCollection,
  viewShardsCollection,
  viewersCollection,
  creatorQuotaDoc,
  assetGenJobDoc,
  assetVariantDoc,
  platformSettingsDoc,
  qualityPublicationDoc,
  qualityRunDoc,
  compositionPublicActiveDocV2,
  compositionBuildDocV2,
  compositionPublicCompletionDocV2,
  compositionRuntimeControlDoc,
} from '../paths.js';
import { LEGACY_BUILD_PIPELINE_REF, buildPipelineRef, withBuildPipelineSeeds, resolveBuildPipeline, type BuildPipelineCatalog, type BuildPipelineRef, type BuildPipelineSnapshot } from '../buildPipelines.js';
import { resolveBuildExecutionPolicy, type BuildAcceptancePolicyMode } from '../buildExecutionPolicy.js';
import { SLUG_MAX_LEN, isValidSlug, randomSlugSuffix } from '../slug.js';
import { score as trendingScore } from '../trending.js';
import { SETTINGS_SCHEMA_VERSION } from '../spec/registry.js';
import { queuedVersionWrite, toVersionAssetRefs, type AppendVersionInput } from './versions.js';
import { abortRunningTraceSegments, advanceLedger, createMeasuredBuildExecution, queuedBuildJobWrite, type BuildClaimAuthority } from './buildJobs.js';
import { compositionMeasuredLimits, compositionSuccessorMediaWindow, isCompositionMeasuredLimits } from './measuredBuildWindow.js';
import { requireFailedBuildCheckpoint, sameFailedCheckpoint, measuredFailedBuildCheckpoint } from '../buildRecovery.js';
import { AssetRejectionsRepo } from './assetRejections.js';
import { AssetGenJobsRepo, cancelBoundCompositionMediaChild, type EnqueueAssetGenJobInput } from './assetGenJobs.js';
import { compositionMediaResumeJournal } from './compositionMediaResume.js';
import { compositionRecipeRevisionV1 } from './compositionRoute.js';
import { QualityLifecycleRepo } from './qualityLifecycle.js';
import type { CompositionPublicCompletionV2, CompositionV2LifecycleV2 } from '../compositionPublicCompletionV2.js';
import { PLATFORM_MUSIC_QUOTA_UID } from '../audioGeneration.js';
import { normalizePlatformSettings } from './platformSettings.js';
import { assertQualityControlRevisionV1, writeQualityAdmissionV2, type QualityAdmissionWriteV2 } from '../qualityAdmission.js';
import {
  canonicalJson,
  createGameplayCapabilityLock,
  verifyGameplayCapabilityLock,
  type GameplayCapabilityLockV1,
} from '@djgg/gameplay-kit';
import type { GameSettings, PartialGameSettings } from '../spec/types.js';
import type {
  Creation,
  CreationAsset,
  CreationEditorsPick,
  CreationStyle,
  ConceptAnalysisDraft,
  ConceptAnalysisCanonicalInputs,
  ConceptAnalysisEvidenceDigest,
  ConceptAnalysisFailure,
  ConceptAnalysisArtifactSummary,
  ConceptAnalysisResult,
  ConceptManualDesignDraft,
  BuildCommandRecord,
  BuildJob,
  AssetVariantDoc,
  MeasuredBuildExecutionV1,
  AssetGenJob,
  CreationVersion,
  VersionGates,
  EditorEntry,
  EngineRef,
  PlatformSettings,
  TemplateFamily,
  Visibility,
} from '../types.js';
import { CONCEPT_ANALYSIS_MAX_CLARIFICATION_CYCLES, isUnbuiltDraft } from '../types.js';
import { validateGameDesign, type GameDesign, type ReferenceBrief, type VideoBrief } from '../design/types.js';
import type { CreationRating } from '../rating/evaluate.js';
import type { QualityRunRecordV2 } from './qualityRuns.js';
import type { QualityPublicationRecordV1 } from './qualityPublication.js';

/** Bounded retries for the assets-array CAS loops (attach/detach). */
const ASSET_CAS_RETRIES = 3;
const ANALYSIS_CAS_RETRIES = 5;
const ANALYSIS_STALE_HISTORY_MAX = 20;

/** The assets array's CAS signature: the ORDERED variantId sequence. Every
 *  mutation of the array (append, replace-in-place, detach) changes it, so a
 *  precondition pinning it covers everything a read-compute-write of the
 *  array was decided from. */
const assetSig = (assets: readonly CreationAsset[]): string =>
  assets.map((a) => a.variantId).join('\n');

/** Fence every nested analysis field. Composed Create checkpoints and review
 * preference changes both replace analysisDraft, so a shallow operation
 * signature could let either writer restore stale state from another tab. */
const analysisSig = (draft: ConceptAnalysisDraft | undefined): string =>
  canonicalJson(JSON.parse(JSON.stringify(draft ?? null)));

/** Whether an analysis's pinned input still matches the creation's title,
 * concept and saved settings. An edit makes the analysis stale without touching it. */
export function analysisInputsCurrent(creation: Pick<Creation, 'title' | 'concept' | 'settingsRev'> | null | undefined,
  inputs: ConceptAnalysisCanonicalInputs): boolean {
  return !!creation && creation.title.trim() === inputs.title && (creation.concept ?? '').trim() === inputs.concept &&
    (inputs.settingsRevision === undefined || (creation.settingsRev ?? 0) === inputs.settingsRevision);
}

function analysisArtifactSummaries(
  result: ConceptAnalysisResult | undefined,
  references: readonly ReferenceBrief[],
  videos: readonly VideoBrief[],
): ConceptAnalysisArtifactSummary[] {
  const artifacts: ConceptAnalysisArtifactSummary[] = references.map((brief) => ({
    id: `reference:${brief.url}`,
    kind: 'reference',
    state: brief.status === 'ok' ? 'ready' : 'unavailable',
    title: (brief.title || brief.url).slice(0, 160),
    summary: (brief.status === 'ok'
      ? brief.summary
      : 'This reference could not be summarized. The pitch can continue without it.').slice(0, 500),
  }));
  artifacts.push(...videos.map((brief) => ({
    id: `video:${brief.videoId}`,
    kind: 'video' as const,
    state: brief.status === 'ok' ? 'ready' as const : 'unavailable' as const,
    title: (brief.title || brief.game).slice(0, 160),
    summary: (brief.observations?.summary ?? 'The gameplay preview was unavailable, so it was not used.').slice(0, 500),
    ...(brief.status === 'ok'
      ? { thumbnailUrl: `https://i.ytimg.com/vi/${encodeURIComponent(brief.videoId)}/mqdefault.jpg` }
      : {}),
  })));
  const proposal = result?.kind === 'proposal'
    ? result.proposal
    : result && 'draftProposal' in result ? result.draftProposal : undefined;
  if (proposal) {
    artifacts.push({ id: 'design:play', kind: 'gameplay-idea', state: 'ready',
      title: 'How you play', summary: proposal.design.summary.slice(0, 500) },
    { id: 'design:goal', kind: 'gameplay-idea', state: 'ready',
      title: 'Your challenge', summary: proposal.design.winCondition.slice(0, 500) });
    artifacts.push(...proposal.design.mechanics.slice(0, 6).map((mechanic, index) => ({
      id: `gameplay-idea:${index}`,
      kind: 'gameplay-idea' as const,
      state: 'ready' as const,
      title: `Gameplay idea ${index + 1}`,
      summary: mechanic.slice(0, 500),
    })));
    const words = [...proposal.design.coreVerbs, ...proposal.design.entities.map((entity) => entity.name)];
    artifacts.push(...[...new Set(words)].slice(0, 12).map((word) => ({
      id: `vocabulary:${word.toLowerCase()}`,
      kind: 'vocabulary' as const,
      state: 'ready' as const,
      title: word.slice(0, 160),
      summary: 'Part of the proposed game vocabulary.',
    })));
  }
  return artifacts.slice(0, 30);
}

export interface ClaimConceptAnalysisInput {
  inputs: ConceptAnalysisCanonicalInputs;
  operationId: string;
  claimantId: string;
  leaseMs: number;
  answers?: Record<string, string>;
  /** Deej answers for a creator who left the questions alone; the creator's
   * own answers can still replace these while they are being analyzed. */
  answeredForCreator?: boolean;
}

export type ClaimConceptAnalysisResult =
  | { kind: 'claimed'; draft: ConceptAnalysisDraft }
  | { kind: 'busy' | 'reused'; draft: ConceptAnalysisDraft }
  | { kind: 'input-changed' };

export interface ConceptAnalysisClaimToken {
  operationId: string;
  claimantId: string;
  attempt: number;
}

export type ConsumeAnalysisQuotaResult =
  | { kind: 'granted'; draft: ConceptAnalysisDraft }
  | { kind: 'denied'; draft: ConceptAnalysisDraft }
  | { kind: 'lost' };

export interface AcceptBuildInput {
  composition?: AcceptedCompositionV1;
  compositionAdmissionFence?: CompositionAdmissionFenceV1;
  /** A composed REVISION of the live game: the build replaces live version
   * `baseVersion` (whose accepted composition digest is
   * `baseAuthorityDigest`, null for a generic game) with `composition`. The
   * revision authority and its art carry are derived here, under the
   * creation lock, from that exact live version. */
  compositionRevision?: { baseVersion: number; baseAuthorityDigest: string | null };
  pipeline?: BuildPipelineRef;
  /** Handler-resolved snapshot used to compose media fan-out. Acceptance must
   * observe this exact snapshot or reject the whole command. */
  expectedPipelineSnapshot?: BuildPipelineSnapshot;
  /** Coherent Admin rollout snapshot used while the handler prepares media.
   * Acceptance re-reads it under the durable-job transaction so a concurrent
   * rollout disable cannot accept fresh provider work. */
  expectedPlatformSettings: Pick<PlatformSettings, 'revision' | 'creationExperience'>;
  creationId: string;
  commandId: string;
  requestFingerprint: string;
  callerUid: string;
  callerHandle: string;
  actionPerformedBy: string;
  actionPerformedByHandle: string;
  jobId: string;
  version: Omit<AppendVersionInput, 'creationId'>;
  expectedLatestVersion: number;
  expectedActiveVersion: number;
  expectedSettingsRev: number;
  expectedDesignRev: number;
  expectedAnalysisOperationId?: string;
  expectedAnalysisRevision?: number;
  requireFreshProposal?: boolean;
  quota: { uid: Uid; day: string; cap: number };
  musicQuota?: { perCreationLifetime: number; perCreatorDay: number; platformPerDay: number };
  defaultDescription?: string;
  auditDetails?: Record<string, string | number>;
  /** Independent media work accepted atomically with the code build. */
  mediaJobs?: EnqueueAssetGenJobInput[];
  /** Shared rev2 execution authority created in this same transaction. */
  measuredExecution?: MeasuredBuildExecutionV1;
  /** Exact intended payloads for reused repair branches. */
  reusedMediaAuthority?: {
    visualPlan?: NonNullable<AssetGenJob['plan']>;
    musicBrief?: NonNullable<AssetGenJob['audioBrief']>;
  };
  /** Dormant v2 authority, revalidated and committed with version + job. */
  qualityAdmissionV2?: QualityAdmissionWriteV2;
  /** Fences legacy/V2 route selection against concurrent Admin rollout edits. */
  qualityControlRevision?: number | null;
}

export interface AcceptBuildResult extends BuildCommandRecord {
  reused: boolean;
}

/** One creator-approved deterministic asset publish. Unlike `acceptBuild`, it
 * burns no build quota and awards no iteration badge because no model edits
 * the game. The active source, asset inventory, version, job, and build-state
 * flip are nevertheless accepted under the same creation lock. */
/** Who started an asset rebind: the creator's studio publish, or the
 * builder's automatic art completion binding judged follow-up art (owner
 * decision 2026-10-21). One route; the trigger only names the audit row. */
export type AssetRebindTrigger = 'creator-approved-assets' | 'automatic-art-completion';

const ASSET_REBIND_AUDIT_ACTION: Record<AssetRebindTrigger, string> = {
  'creator-approved-assets': 'build.assetPublish',
  'automatic-art-completion': 'build.artCompletion',
};

export interface AcceptAssetRebindInput {
  trigger: AssetRebindTrigger;
  workshop?: { originVersion: number; revision: number };
  workshopAssets?: CreationAsset[];
  /** The active version's accepted game admitted on the current released
   * runtime by the trusted admission ({@link admitCompositionSuccessorV1}). */
  composition?: AcceptedCompositionV1;
  compositionAdmissionFence?: CompositionAdmissionFenceV1;
  creationId: string;
  jobId: string;
  auditId: string;
  commandId: string;
  requestFingerprint: string;
  callerUid: string;
  callerHandle: string;
  actionPerformedBy: string;
  actionPerformedByHandle: string;
  expectedLatestVersion: number;
  expectedActiveVersion: number;
  expectedAssetVariantIds: string[];
  expectedAssetPolicyRevision?: number;
  version: Omit<AppendVersionInput, 'creationId'>;
  qualityAdmissionV2?: QualityAdmissionWriteV2;
  qualityControlRevision?: number | null;
}

/**
 * The ONE construction of a composed asset rebind's version input, for both
 * triggers (the studio composed publish and the builder's automatic art
 * completion): a zero-model `asset-rebind` copy of the live `seed` that binds
 * `assets` over it, credited to the attributed member. The repository still
 * re-derives everything authority-bearing from the locked seed inside
 * {@link CreationsRepo.acceptAssetRebind}; this only fixes what the queued
 * version records.
 */
export function composedAssetRebindVersion(input: {
  trigger: AssetRebindTrigger;
  seed: CreationVersion;
  /** The creation's attached inventory the rebind is accepted against. */
  inventory: readonly CreationAsset[];
  assets: NonNullable<CreationVersion['artCompletionAssets']>;
  /** Attached changes this rebind leaves out because they are not media slots. */
  notPublished: readonly string[];
  updatedBy: string;
  updatedByHandle: string;
}): AcceptAssetRebindInput['version'] {
  const { seed } = input;
  if (!seed.srcPath) throw new PlatformError('conflict', 'the active game has no reusable live source archive');
  const names = input.assets.map(asset => asset.name).join(', ');
  const leftOut = input.notPublished.length ? `. Not published (not media slots in this game): ${input.notPublished.join(', ')}` : '';
  return {
    prompt: seed.prompt,
    provider: 'copy',
    model: 'copy',
    diffSummary: input.trigger === 'automatic-art-completion'
      ? `Bound judged follow-up media: ${names}${leftOut}`
      : `Published approved media: ${names}${leftOut}`,
    srcPath: seed.srcPath,
    buildMode: 'asset-rebind',
    ...(seed.pipelineSnapshot ? { pipelineSnapshot: seed.pipelineSnapshot } : {}),
    updatedBy: input.updatedBy,
    updatedByHandle: input.updatedByHandle,
    ...(seed.settingsSnapshot
      ? {
          settingsSnapshot: seed.settingsSnapshot,
          settingsSchemaVersion: seed.settingsSchemaVersion,
          settingsRev: seed.settingsRev,
          settingsDiff: '',
        }
      : {}),
    ...(seed.designSnapshot ? { designSnapshot: seed.designSnapshot, designRev: seed.designRev } : {}),
    assetsSnapshot: toVersionAssetRefs(input.inventory),
    artCompletionOf: seed.n,
    artCompletionAssets: input.assets,
  };
}

export interface AcceptAssetRebindResult {
  creationId: string;
  version: number;
  jobId: string;
  reused: boolean;
}

interface StoredBuildCommandLedger {
  action: string;
  creationId: string;
  actionPerformedBy: string;
  details: Record<string, string | number>;
}

const buildCommandLedgerPath = (creationId: string, commandId: string): string =>
  actionLogDoc(`build-command:${encodeURIComponent(creationId)}:${encodeURIComponent(commandId)}`);

function acceptedBuildResult(
  creationId: string,
  commandId: string,
  prior: StoredBuildCommandLedger,
): AcceptBuildResult {
  const details = prior.details ?? {};
  return {
    creationId,
    commandId,
    callerUid: prior.actionPerformedBy,
    requestFingerprint: String(details.requestFingerprint),
    version: Number(details.version),
    jobId: String(details.jobId),
    quotaIdentity: String(details.quotaIdentity),
    dispatchState: 'queued',
    acceptedAt: Number(details.acceptedAt ?? 0),
    reused: true,
  };
}

export interface CompleteConceptAnalysisInput extends ConceptAnalysisClaimToken {
  result: ConceptAnalysisResult;
  referenceBriefs: ReferenceBrief[];
  videoBriefs: VideoBrief[];
  evidenceDigests: ConceptAnalysisEvidenceDigest[];
}

export interface SaveConceptManualDraftInput {
  expectedRevision: number;
  sourceRevision: number;
  title: string;
  concept: string;
  design: Partial<GameDesign>;
  unresolvedFields: string[];
}

/** The claim doc stored at `slugs/{slug}`. */
export interface SlugClaim {
  creationId: string;
  ownerUid: Uid;
  claimedAt: number;
}

/** Fields the caller supplies to create a creation (the rest are defaulted). */
export interface CreateCreationInput {
  creationId: string;
  createRequestFingerprint?: string;
  slug: string;
  title: string;
  description: string;
  style: CreationStyle;
  creatorUid: Uid;
  creatorHandle: string;
  template: TemplateFamily;
  engine: EngineRef;
  visibility?: Visibility;
  /** Initial ANSWERED wizard decisions (draft-valid; the createCreation
   *  handler validates via spec/checkSettings before calling). */
  settings?: PartialGameSettings;
  /** The creator's verbatim free-text concept (concept-first creation). */
  concept?: string;
}

/** Fields a creator/owner may update on a creation. `concept` is the
 *  creator's verbatim free-text (concept-first creation) — editable while
 *  drafting so analyzeConcept always reads what the creator last wrote. */
export type UpdateCreationInput = Partial<
  Pick<Creation, 'title' | 'description' | 'style' | 'visibility' | 'concept' | 'avoidDirection'>
>;

/**
 * Input for {@link CreationsRepo.ensureNative} — the manifest-owned fields of a
 * NATIVE game's creation doc (the portal's native-game manifest is the source;
 * the seed tool passes them through).
 */
export interface EnsureNativeInput {
  slug: string;
  title: string;
  description: string;
  style: CreationStyle;
  /** Platform identity the native is credited to (stamped on CREATE only —
   *  identity never moves on a re-seed; that would be ops surgery). */
  creatorUid: Uid;
  creatorHandle: string;
}

/** Doc id of a native game's creation doc: `native-<slug>` — namespaced so it
 *  can never collide with a generated creation id, while `/g/<slug>` keeps
 *  resolving through the normal slug-claim path. */
export function nativeCreationId(slug: string): string {
  return `native-${slug}`;
}

/**
 * Input for {@link CreationsRepo.createFork}. The HANDLER owns the parent
 * gates (kind='creation', visibility='public', a live version with a source
 * archive) and passes the validated parent through; the repo owns the fork's
 * data shape — lineage, roster, counters, the draft state, and the one-batch
 * atomicity with its seeded v1 + build job.
 */
export interface CreateForkInput {
  seedPipelineSnapshot?: CreationVersion['pipelineSnapshot'];
  executionPolicyMode?: BuildAcceptancePolicyMode;
  qualityRun?: CreationVersion['qualityRun'];
  qualityAdmissionV2?: QualityAdmissionWriteV2;
  qualityControlRevision?: number | null;
  /** The seed's accepted game admitted on the current released runtime (the
   * trusted server admission's {@link admitCompositionSuccessorV1}). The
   * fork's authority; required for a composed seed. */
  composition?: AcceptedCompositionV1;
  compositionAdmissionFence?: CompositionAdmissionFenceV1;
  /** Exact trusted lock on the selected immutable seed version. The caller
   * passes the row's stored value; no code infers it from a source path or an
   * active pointer. */
  seedCapabilityLock?: CreationVersion['capabilityLock'];
  /** Minted id for the fork (the handler's genId). */
  forkId: string;
  /** The parent's creation id ({@link Creation} carries no own-id field). */
  parentId: string;
  /** The validated PUBLIC parent this fork copies. */
  parent: Creation;
  /** The parent version the fork seeds from (the parent's activeVersion). */
  seedVersion: number;
  /** The parent version's source archive — the fork v1's `srcPath`. Pointing
   *  at ANOTHER creation's archive is what routes the builder into the no-LLM
   *  copy build. */
  seedSrcPath: string;
  /** Required creative direction for v1. It makes a fork a remix, not a
   * wasteful duplicate that must wait for v1 before it can change. */
  initialIntent?: string;
  /** The seed version's settings snapshot, re-stamped on the fork's v1 so the
   *  pending-changes baseline carries over (absent when the parent predates
   *  settings snapshots). */
  seedSettingsSnapshot?: GameSettings;
  seedSettingsSchemaVersion?: number;
  /** The seed version's design snapshot (?? the parent head), re-stamped on
   *  the fork's v1 — the design lineage follows the source lineage. */
  seedDesignSnapshot?: GameDesign;
  /** The `designRev` that {@link CreateForkInput.seedDesignSnapshot} corresponds
   *  to — carried forward so the fork's v1 snapshot rev matches the source it
   *  seeded from (the parent's active version, ?? the parent head), never a
   *  reset-to-1. */
  seedDesignRev?: number;
  /** The forker — the fork's creator (roster of one; origin R4). */
  forkerUid: Uid;
  forkerHandle: string;
  /** Minted id for the fork's v1 copy-build job. */
  jobId: string;
  /** Build quota charged in the same transaction as fork admission. Replays
   * of the deterministic fork id return without consuming another unit. */
  quota?: { uid: Uid; day: string; cap: number };
  /** Slug-suffix source, injected for deterministic tests; defaults to
   *  {@link randomSlugSuffix}. */
  genSuffix?: () => string;
}

/** Slug-mint attempts before {@link CreationsRepo.createFork} gives up. */
export const FORK_SLUG_ATTEMPTS = 3;

/** Postgres unique-violation on the creations slug — the concurrent-mint race
 *  {@link CreationsRepo.createFork} retries past (the claim-doc pre-read wins
 *  everywhere else, in-memory store included). */
function isSlugUniqueViolation(e: unknown): boolean {
  const err = e as { code?: string; constraint?: string };
  return err?.code === '23505' && (err.constraint ?? '').includes('slug');
}

async function assertForkReplay(
  input: CreateForkInput,
  forkIntent: string,
  existing: Creation,
  version: CreationVersion | null,
  job: BuildJob | null,
): Promise<void> {
  if (existing.creatorUid !== input.forkerUid || existing.forkParentId !== input.parentId) {
    throw new PlatformError('conflict', `creation ${input.forkId} belongs to another fork command`);
  }
  const lineage = version?.executionPolicy?.compositionLineage;
  let accepted = false;
  if (lineage && version?.executionPolicy?.composition) {
    try {
      const checked = await validateCompositionLineageV1(lineage,
        await validateAcceptedCompositionV1(version.executionPolicy.composition));
      accepted = !forkIntent && checked.kind === 'fork-copy'
        && checked.predecessor.creationId === input.parentId
        && version.buildMode === 'copy' && version.creationRun?.codeJobId === input.jobId
        && job?.creationId === input.forkId && job.version === 1
        && job.measuredExecution?.compositionDigest === checked.authorityDigest
        && job.measuredExecution?.compositionLineageDigest === checked.digest;
    } catch {
      accepted = false;
    }
  } else {
    accepted = !!version && !lineage && version.prompt === forkIntent
      && version.buildMode === (forkIntent ? 'fork-remix' : undefined)
      && job?.creationId === input.forkId && job.version === 1
      && !job.measuredExecution?.compositionDigest && !job.measuredExecution?.compositionLineageDigest;
  }
  if (!accepted) throw new PlatformError('conflict', `creation ${input.forkId} belongs to another fork command`);
}

/** Sort orders for {@link CreationsRepo.list}. */
export type ListSort = 'trending' | 'newest' | 'liked' | 'played';

/** Filters for {@link CreationsRepo.list}. */
export interface ListCreationsFilter {
  style?: CreationStyle;
  creatorUid?: Uid;
  /** Creations the uid can EDIT (creator or editor) — matches `memberUids`
   *  via array-contains. Mutually exclusive with `creatorUid` in practice. */
  memberUid?: Uid;
  visibility?: Visibility;
  sort?: ListSort;
  limit?: number;
}

export type TerminatedBuildKind = 'composition-v1' | 'composition-v2' | 'verified-core-v2' | 'generic';

/** Lifecycle-safe codes recording who terminated a Composition V2 build. */
const TERMINATION_SAFE_CODES = {
  cancel: 'cancelled',
  'admin-fail': 'admin-failed',
  reaper: 'execution-expired',
} as const;

function terminatedBuildKind(job: BuildJob, version: CreationVersion | null): TerminatedBuildKind {
  if (job.requiredWorkerCapability === 'verified-core-v2') return 'verified-core-v2';
  if (job.requiredWorkerCapability === 'composition-private-v2') return 'composition-v2';
  if (!job.requiredWorkerCapability
    && (job.measuredExecution?.compositionDigest || version?.executionPolicy?.composition)) return 'composition-v1';
  return 'generic';
}

export class CreationsRepo {
  constructor(
    private readonly store: DocStorePort & Partial<DocTxRunner>,
    /** Reference clock for trending sort (injected for determinism). */
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** Forward-only retirement of generic queued/claimed work. Published rows,
   * immutable artifacts and newer creation heads are never rewritten. */
  async retireGenericBuild(docTx: DocTxRunner, jobId: string): Promise<boolean> {
    return docTx.runTransaction(async tx => {
      const job = await tx.get<BuildJob>(buildJobDoc(jobId));
      if (!job || job.status === 'done' || job.status === 'failed') return false;
      const creation = await tx.get<Creation>(creationDoc(job.creationId));
      const version = await tx.get<CreationVersion>(versionDoc(job.creationId, job.version));
      if (version?.executionPolicy?.composition || job.requiredWorkerCapability === 'composition-private-v2') return false;
      if (job.requiredWorkerCapability === 'verified-core-v2') {
        throw new PlatformError('conflict', 'Verified-quality retirement requires its lifecycle authority');
      }
      const ts = this.store.serverTimestamp();
      await tx.update(buildJobDoc(jobId), { status: 'failed', error: GENERIC_CREATION_RETIRED,
        workerId: null, claimToken: null, leaseExpiry: null, heartbeatAt: null, finishedAt: ts });
      if (version && version.buildStatus !== 'live' && version.buildStatus !== 'failed') {
        await tx.update(versionDoc(job.creationId, job.version), { buildStatus: 'failed',
          gates: { ...version.gates, notes: GENERIC_CREATION_RETIRED }, finishedAt: ts });
        for (const mediaId of [version.creationRun?.visualJobId, version.creationRun?.musicJobId]) {
          if (!mediaId) continue;
          const media = await tx.get<AssetGenJob>(assetGenJobDoc(mediaId));
          if (media && ['awaiting-core', 'queued', 'running'].includes(media.status)) {
            await tx.update(assetGenJobDoc(mediaId), { status: 'failed', error: GENERIC_CREATION_RETIRED,
              failureKind: 'cancelled', workerId: null, heartbeatAt: null, finishedAt: ts });
          }
        }
      }
      if (creation?.latestVersion === job.version && creation.buildState === 'building') {
        await tx.update(creationDoc(job.creationId), { buildState: 'idle',
          status: creation.activeVersion > 0 && creation.activeBundleUrl ? 'live' : 'failed', updatedAt: ts });
      }
      return true;
    });
  }

  /** The one termination of an in-flight build — creator cancel, admin
   * force-fail and the reaper's terminal branch. Legacy verified-core@2 and
   * generic work keep their own lifecycle authorities. Composed work (V1 and
   * Composition V2) terminates in ONE transaction locked in the global V2
   * order: runtime control → job → creation → version → V2 build → V2
   * completion → media children (sorted). Ledger reservations are left as
   * they are: reserved spend stays charged and late settlement stays
   * authorised. A `done` job is never touched; a `failed` job still has its
   * non-terminal version, creation head, media and V2 lifecycle finished. */
  async terminateBuild(
    docTx: DocStorePort & DocTxRunner,
    input: { jobId: string; creationId: string; version: number;
      initiator: 'cancel' | 'admin-fail' | 'reaper'; reason: string },
  ): Promise<{ outcome: 'terminated' | 'already-terminal'; kind: TerminatedBuildKind }> {
    const observed = await this.store.getDoc<BuildJob>(buildJobDoc(input.jobId));
    if (!observed) throw new PlatformError('not-found', `build job ${input.jobId} not found`);
    if (observed.creationId !== input.creationId || observed.version !== input.version) {
      throw new PlatformError('conflict', `build job ${input.jobId} does not own ${input.creationId} v${input.version}`);
    }
    const observedVersion = await this.store.getDoc<CreationVersion>(versionDoc(input.creationId, input.version));
    const kind = terminatedBuildKind(observed, observedVersion);
    if (kind === 'verified-core-v2') {
      if (observed.status === 'done') return { outcome: 'already-terminal', kind };
      const lifecycle = new QualityLifecycleRepo(docTx, this.now);
      const identity = { jobId: input.jobId, creationId: input.creationId, version: input.version };
      if (input.initiator === 'cancel') await lifecycle.cancel(identity, input.reason);
      else await lifecycle.fail(identity, input.reason);
      return { outcome: observed.status === 'failed' ? 'already-terminal' : 'terminated', kind };
    }
    if (kind === 'generic') {
      return { outcome: await this.retireGenericBuild(docTx, input.jobId) ? 'terminated' : 'already-terminal', kind };
    }
    const reason = input.reason.slice(0, 500);
    const safeErrorCode = TERMINATION_SAFE_CODES[input.initiator];
    return docTx.runTransaction(async tx => {
      await tx.get(compositionRuntimeControlDoc());
      const job = await tx.get<BuildJob>(buildJobDoc(input.jobId));
      const creation = await tx.get<Creation>(creationDoc(input.creationId));
      const version = await tx.get<CreationVersion>(versionDoc(input.creationId, input.version));
      if (!job || !version || terminatedBuildKind(job, version) !== kind || job.creationId !== input.creationId
        || job.version !== input.version || version?.creationRun?.codeJobId !== input.jobId) {
        throw new PlatformError('conflict', 'composed build termination authority changed');
      }
      if (job.status === 'done') return { outcome: 'already-terminal' as const, kind };
      const lifecycle = job.compositionV2Lifecycle;
      const buildId = job.measuredExecution?.compositionV2Acceptance?.buildId;
      if (buildId) await tx.get(compositionBuildDocV2(buildId));
      const completionPath = lifecycle?.completionId ? compositionPublicCompletionDocV2(lifecycle.completionId) : null;
      const completion = completionPath ? await tx.get<CompositionPublicCompletionV2>(completionPath) : null;
      if (completionPath && !completion) throw new PlatformError('conflict', 'Composition V2 completion intent is missing');
      const ts = this.store.serverTimestamp();
      const now = this.now();
      const mediaIds = kind === 'composition-v1'
        ? [version.creationRun?.visualJobId, version.creationRun?.musicJobId]
          .filter((id): id is string => !!id).sort()
        : [];
      for (const mediaId of mediaIds) {
        await cancelBoundCompositionMediaChild(tx, { parentJobId: input.jobId, parent: job, jobId: mediaId,
          reason, finishedAt: ts });
      }
      const alreadyFailed = job.status === 'failed';
      const run = job.measuredExecution;
      const openLifecycle = lifecycle && ['private-build', 'public-completion'].includes(lifecycle.stage);
      if (!alreadyFailed || openLifecycle) {
        const epoch = (lifecycle?.wake.epoch ?? 0) + 1;
        await tx.update(buildJobDoc(input.jobId), {
          ...(alreadyFailed ? {} : { status: 'failed', workerId: null, claimToken: null, leaseExpiry: null,
            heartbeatAt: null, error: reason, finishedAt: ts }),
          ...(kind === 'composition-v1' && run && !alreadyFailed
            ? { measuredExecution: advanceLedger(run, { traceSegments: abortRunningTraceSegments(run, 'every-claim', now) }) }
            : {}),
          ...(lifecycle && openLifecycle ? { compositionV2Lifecycle: {
            ...lifecycle, stage: 'cancelled', revision: lifecycle.revision + 1,
            wake: { id: `v2-wake:${input.jobId}:${epoch}`, epoch, state: 'dispatched', createdAt: now, dispatchedAt: now },
            safeErrorCode, updatedAt: now,
          } satisfies CompositionV2LifecycleV2 } : {}),
        });
      }
      if (completion && openLifecycle && !['published', 'blocked', 'cancelled'].includes(completion.phase)) {
        await tx.update(completionPath!, { phase: 'cancelled', revision: completion.revision + 1,
          safeErrorCode, updatedAt: Math.max(now, completion.updatedAt) });
      }
      if (version.buildStatus !== 'live' && version.buildStatus !== 'failed') {
        await tx.update(versionDoc(input.creationId, input.version), { buildStatus: 'failed',
          gates: { ...version.gates, notes: reason, terminatedBy: input.initiator }, finishedAt: ts });
      }
      if (creation?.latestVersion === input.version && creation.buildState === 'building') {
        await tx.update(creationDoc(input.creationId), { buildState: 'idle',
          status: creation.activeVersion > 0 && creation.activeBundleUrl ? 'live' : 'failed', updatedAt: ts });
      }
      return { outcome: alreadyFailed ? 'already-terminal' as const : 'terminated' as const, kind };
    });
  }

  /** Pre-upload check; publication rechecks under its authority lock/CAS. */
  async assertMaterializedMediaAllowed(creationId: string, inventory: unknown): Promise<void> {
    const creation = await this.get(creationId);
    if (!creation) throw new PlatformError('not-found', 'creation not found');
    await new AssetRejectionsRepo(this.store).assertMaterializedAllowed(creationId, inventory, creation.assetPolicyRevision ?? 0);
  }

  /**
   * Create a creation, atomically claiming its slug. Throws
   * `PlatformError('validation')` on a malformed slug and
   * `PlatformError('conflict')` when the slug is already claimed.
   */
  async create(input: CreateCreationInput): Promise<Creation> {
    return (await this.createWithReceipt(input)).creation;
  }

  async createWithReceipt(input: CreateCreationInput): Promise<{ creation: Creation; reused: boolean }> {
    if (!isValidSlug(input.slug)) {
      throw new PlatformError('validation', `create: invalid slug ${JSON.stringify(input.slug)}`);
    }

    const existingClaim = !input.createRequestFingerprint && await this.store.getDoc<SlugClaim>(slugDoc(input.slug));
    if (existingClaim) {
      throw new PlatformError('conflict', `slug ${input.slug} already claimed`);
    }
    if (!input.createRequestFingerprint && await this.store.getDoc<Creation>(creationDoc(input.creationId))) {
      throw new PlatformError('conflict', `creation ${input.creationId} already exists`);
    }

    const ts = this.store.serverTimestamp();
    const creationWrite: Record<string, unknown> = {
      slug: input.slug,
      title: input.title,
      description: input.description,
      style: input.style,
      creatorUid: input.creatorUid,
      creatorHandle: input.creatorHandle,
      // Collaboration: a creation starts with no editors; memberUids is the
      // server-owned [creator, ...editors] index (see setEditors).
      editors: [],
      memberUids: [input.creatorUid],
      template: input.template,
      engine: { provider: input.engine.provider, model: input.engine.model },
      status: 'draft',
      latestVersion: 0,
      latestBundleUrl: '',
      activeVersion: 0,
      activeBundleUrl: '',
      likesCount: 0,
      commentsCount: 0,
      viewsCount: 0,
      distinctViewers: 0,
      playsCount: 0,
      lastPlayedAt: 0,
      // Born UNLISTED (the publish gate): link-shareable and playable, but not
      // listed until a member publishes (the unlisted→public flip). Trusted
      // callers (seed/ops) may still pass an explicit visibility.
      visibility: input.visibility ?? 'unlisted',
      // Wizard decisions: sparse answered values + schema stamp + revision.
      // Rev starts at 1 — the initial answer set (even empty) IS a revision,
      // so the pending-changes comparison never special-cases creation.
      settings: { ...(input.settings ?? {}) },
      settingsSchemaVersion: SETTINGS_SCHEMA_VERSION,
      settingsRev: 1,
      ...(input.concept ? { concept: input.concept } : {}),
      ...(input.createRequestFingerprint ? { createRequestFingerprint: input.createRequestFingerprint } : {}),
      createdAt: ts,
      updatedAt: ts,
    };
    const claimWrite: Record<string, unknown> = {
      creationId: input.creationId,
      ownerUid: input.creatorUid,
      claimedAt: ts,
    };

    if (input.createRequestFingerprint) {
      if (!/^[a-f0-9]{64}$/.test(input.createRequestFingerprint)) throw new PlatformError('validation', 'invalid draft request fingerprint');
      if (!this.store.runTransaction) throw new PlatformError('unavailable', 'draft recovery requires server transactions');
      const reused = await this.store.runTransaction(async tx => {
        // Postgres locks even an absent creation path: concurrent retries
        // cannot overwrite the draft, its edits, or its membership roster.
        const prior = await tx.get<Creation>(creationDoc(input.creationId));
        if (prior) {
          if (prior.creatorUid !== input.creatorUid || prior.createRequestFingerprint !== input.createRequestFingerprint) {
            throw new PlatformError('conflict', 'draft request already used for different input');
          }
          return true;
        }
        if (!tx.query) throw new PlatformError('unavailable', 'draft recovery requires transactional queries');
        if ((await tx.query<Creation>(creationsCollection(), { where: [['slug', '==', input.slug]], limit: 1 })).length) {
          throw new PlatformError('conflict', `slug ${input.slug} already claimed`, {
            details: { draftRequestRejected: true },
          });
        }
        await tx.set(creationDoc(input.creationId), creationWrite);
        await tx.set(slugDoc(input.slug), claimWrite);
        return false;
      });
      return { creation: (await this.get(input.creationId))!, reused };
    }
    await this.store.batch([
      { type: 'set', path: creationDoc(input.creationId), data: creationWrite },
      { type: 'set', path: slugDoc(input.slug), data: claimWrite },
    ]);

    return { creation: (await this.get(input.creationId))!, reused: false };
  }

  /**
   * UPSERT a NATIVE game's creation doc (`kind: 'native'`, id
   * `native-<slug>`), claiming its slug through the normal claim-doc path so
   * `/g/<slug>` resolution works unchanged. Trusted-writer only (the seed tool
   * over the admin store — `kind` is a rules server-owned field).
   *
   * IDEMPOTENT with MERGE semantics: when the doc already exists, only the
   * manifest-owned display fields (title / description / style) are updated —
   * counters (likes/views/plays), the collaboration roster, visibility, and
   * the creator identity are all preserved, so a re-seed never resets social
   * state. A slug claimed by a FOREIGN creation, or an existing doc of a
   * different kind, aborts with `conflict` rather than being overwritten.
   *
   * Natives have no builder lifecycle: the doc is created LIVE with version 0
   * and empty bundle URLs (the game page iframes the native manifest path),
   * `template: 'native'`, and a `native` engine sentinel.
   */
  async ensureNative(input: EnsureNativeInput): Promise<{ creationId: string; created: boolean }> {
    if (!isValidSlug(input.slug)) {
      throw new PlatformError(
        'validation',
        `ensureNative: invalid slug ${JSON.stringify(input.slug)}`,
      );
    }
    const creationId = nativeCreationId(input.slug);

    const claim = await this.store.getDoc<SlugClaim>(slugDoc(input.slug));
    if (claim && claim.creationId !== creationId) {
      throw new PlatformError(
        'conflict',
        `ensureNative: slug ${input.slug} is claimed by creation ${claim.creationId}`,
      );
    }

    const existing = await this.get(creationId);
    const ts = this.store.serverTimestamp();
    const claimWrite: Record<string, unknown> = {
      creationId,
      ownerUid: input.creatorUid,
      claimedAt: ts,
    };

    if (existing) {
      if ((existing.kind ?? 'creation') !== 'native') {
        throw new PlatformError(
          'conflict',
          `ensureNative: creation ${creationId} exists but is not kind='native'`,
        );
      }
      const ops: Parameters<DocStorePort['batch']>[0] = [
        {
          type: 'update',
          path: creationDoc(creationId),
          data: {
            title: input.title,
            description: input.description,
            style: input.style,
            updatedAt: ts,
          },
        },
      ];
      // Self-heal a missing claim (never expected, but the pair must hold).
      if (!claim) ops.push({ type: 'set', path: slugDoc(input.slug), data: claimWrite });
      await this.store.batch(ops);
      return { creationId, created: false };
    }

    const creationWrite: Record<string, unknown> = {
      kind: 'native',
      slug: input.slug,
      title: input.title,
      description: input.description,
      style: input.style,
      creatorUid: input.creatorUid,
      creatorHandle: input.creatorHandle,
      editors: [],
      memberUids: [input.creatorUid],
      template: 'native',
      engine: { provider: 'native', model: 'native' },
      status: 'live',
      latestVersion: 0,
      latestBundleUrl: '',
      activeVersion: 0,
      activeBundleUrl: '',
      likesCount: 0,
      commentsCount: 0,
      viewsCount: 0,
      distinctViewers: 0,
      playsCount: 0,
      lastPlayedAt: 0,
      visibility: 'public',
      createdAt: ts,
      updatedAt: ts,
    };
    await this.store.batch([
      { type: 'set', path: creationDoc(creationId), data: creationWrite },
      { type: 'set', path: slugDoc(input.slug), data: claimWrite },
    ]);
    return { creationId, created: true };
  }

  /**
   * FORK a public creation (origin R1–R6): one batch writes the fork doc (a
   * full copy of the parent's display/settings surface with ZEROED counters
   * and a roster of just the forker), its slug claim, the seeded `versions/1`
   * (queued, `srcPath` = the parent's archive — the builder's copy-build
   * route), and the v1 build job — so a fork can never exist half-born.
   *
   * The fork is born `visibility: 'draft'` — invisible outside its members
   * until the builder's divergence finalize flips it `unlisted` (the DB
   * trigger makes the server that ONLY exit; publishing is then the owner's
   * studio flip, like any creation). Lineage: `forkedFrom` snapshots the
   * parent for the credit line; `rootCreatorUid/Handle` chain-propagate the
   * ORIGINAL creator (a fork of a fork keeps the root). The parent doc is NOT
   * touched — its `forksCount` increments only at the fork's FIRST publish
   * (count-once, keyed on the publishedAt set-once CAS).
   *
   * Slug: `<parent-slug>-<suffix6>`, re-minted up to {@link FORK_SLUG_ATTEMPTS}
   * times on collision (claim-doc pre-read; the DB's `creations.slug UNIQUE`
   * backstops a concurrent race). Throws `conflict` when exhausted.
   */
  async createFork(input: CreateForkInput): Promise<Creation> {
    const { parent } = input;
    // Fork birth is an immutable write outside VersionsRepo.append. Authenticate
    // the selected seed row's complete lock before any fork/slug/job document is
    // created; a forged digest can never be propagated as trusted authority.
    const seedCapabilityLock = input.seedCapabilityLock
      ? await verifyGameplayCapabilityLock(input.seedCapabilityLock)
      : undefined;
    // Repository callers predating direct remix intentionally retain the
    // legacy copy semantics; the public handler always requires this field.
    const forkIntent = input.initialIntent?.trim() ?? '';
    const existingFork = await this.get(input.forkId);
    if (existingFork) {
      const [version,job]=await Promise.all([
        this.store.getDoc<CreationVersion>(versionDoc(input.forkId,1)),
        this.store.getDoc<BuildJob>(buildJobDoc(input.jobId)),
      ]);
      await assertForkReplay(input,forkIntent,existingFork,version,job);
      return existingFork;
    }
    const seedHint=await this.store.getDoc<CreationVersion>(versionDoc(input.parentId,input.seedVersion));
    const seedCompositionHint=seedHint?.executionPolicy?.composition;
    if (!seedCompositionHint) throw new PlatformError('validation', GENERIC_CREATION_RETIRED);
    const forkComposition = input.composition;
    if (!forkComposition) throw new PlatformError('validation', 'Composed fork requires its admitted composition authority');
    const sourceJobIdHint=seedHint?.executionPolicy?.composition ? seedHint.creationRun?.codeJobId : undefined;
    if (seedHint?.executionPolicy?.composition && !sourceJobIdHint) {
      throw new PlatformError('conflict','Composed fork predecessor job is unavailable');
    }
    const genSuffix = input.genSuffix ?? randomSlugSuffix;
    const ts = this.store.serverTimestamp();

    // Default title: "<parent title> V<forkNumber>" so two cards never read
    // identically in the gallery (owner decision 2026-07-13; the forker can
    // rename any time). The fork number counts EVERY existing fork of this
    // parent — drafts included, so two quick forks don't both mint V1.
    const priorForks = await this.store.query<Creation>(creationsCollection(), {
      where: [['forkParentId', '==', input.parentId]],
    });
    const title = `${parent.title} V${priorForks.length + 1}`;

    for (let attempt = 0; attempt < FORK_SLUG_ATTEMPTS; attempt++) {
      const suffix = genSuffix();
      // clamp the parent part so the whole slug stays valid (3..48 chars)
      const base = parent.slug.slice(0, SLUG_MAX_LEN - suffix.length - 1).replace(/-+$/, '');
      const slug = `${base}-${suffix}`;
      if (!isValidSlug(slug)) {
        throw new PlatformError('validation', `createFork: minted invalid slug ${slug}`);
      }
      if (await this.store.getDoc<SlugClaim>(slugDoc(slug))) continue;

      const forkWrite: Record<string, unknown> = {
        slug,
        title,
        description: parent.description,
        style: parent.style,
        creatorUid: input.forkerUid,
        creatorHandle: input.forkerHandle,
        editors: [],
        memberUids: [input.forkerUid],
        template: parent.template,
        engine: { provider: parent.engine.provider, model: parent.engine.model },
        status: 'draft',
        // the v1 copy build is enqueued in this same batch
        buildState: 'building',
        latestVersion: 1,
        latestBundleUrl: '',
        activeVersion: 0,
        activeBundleUrl: '',
        likesCount: 0,
        commentsCount: 0,
        viewsCount: 0,
        distinctViewers: 0,
        playsCount: 0,
        lastPlayedAt: 0,
        visibility: 'draft',
        // The fork head follows the ACTIVE source lineage. A rolled-back parent
        // may have newer settings on its creation head; copying those would
        // make the fork's editable head disagree with its v1 source/snapshot.
        settings: { ...(input.seedSettingsSnapshot ?? parent.settings ?? {}) },
        settingsSchemaVersion:
          input.seedSettingsSchemaVersion ?? parent.settingsSchemaVersion ?? SETTINGS_SCHEMA_VERSION,
        settingsRev: 1,
        forkParentId: input.parentId,
        rootCreatorUid: parent.rootCreatorUid ?? parent.creatorUid,
        rootCreatorHandle: parent.rootCreatorHandle ?? parent.creatorHandle,
        forkedFrom: {
          slug: parent.slug,
          title: parent.title,
          creator: { uid: parent.creatorUid, handle: parent.creatorHandle },
          editors: (parent.editors ?? []).map((e) => ({ uid: e.uid, handle: e.handle })),
          seedVersion: input.seedVersion,
        },
        forksCount: 0,
        createdAt: ts,
        updatedAt: ts,
      };
      // NO cover inheritance: the fork gets its OWN card art at copy-build
      // publish (the builder's ensureCoverArt) — two identical cards in the
      // gallery were an owner finding (2026-07-13). The `cover` ASSET is that
      // identity, so it is dropped here too; DRAWABLE assets (sprite/background/
      // icon) DO ride along — the copied code references them, and the builder's
      // compose seam re-injects creation.assets on every build.
      const inherited = (parent.assets ?? []).filter((a) => a.kind !== 'cover');
      if (inherited.length) forkWrite.assets = inherited.map((a) => ({ ...a }));
      // The design lineage rides along too — the fork's iterates need the
      // same mechanics authority the parent's builds ran against.
      if (parent.concept) forkWrite.concept = parent.concept;
      const forkDesign = input.seedDesignSnapshot ?? parent.gameDesign;
      if (forkDesign) {
        forkWrite.gameDesign = forkDesign;
        // Carry the revision of whichever authority supplied the fork head.
        // For a rollback this is the active version's immutable snapshot, not
        // the newer parent head; legacy designs still guard at revision 1.
        forkWrite.designRev = input.seedDesignRev ?? parent.designRev ?? 1;
      }
      if (parent.referenceBriefs?.length) {
        forkWrite.referenceBriefs = parent.referenceBriefs.map((b) => ({ ...b }));
      }
      // Watches are inherited for the same reason as the page briefs: the fork
      // starts from the parent's design, so it starts from the parent's
      // grounding too — and re-watching would burn a browser session to
      // re-derive observations we already hold.
      if (parent.videoBriefs?.length) {
        forkWrite.videoBriefs = parent.videoBriefs.map((b) => ({ ...b }));
      }

      const claimWrite: Record<string, unknown> = {
        creationId: input.forkId,
        ownerUid: input.forkerUid,
        claimedAt: ts,
      };
      try {
        if (!this.store.runTransaction) throw new PlatformError('unavailable', 'fork creation requires server transactions');
        await this.store.runTransaction(async tx => {
          const lockedCompositionControl = await lockCompositionAdmissionControlV1(tx, forkComposition);
          // Share one global order with failure and publication: all jobs,
          // then all creations, then all versions, each path-sorted. Postgres
          // also advisory-locks absent paths, so the deterministic target
          // serializes competing first admissions before either can upsert.
          const lockedJobs=new Map<string,BuildJob>();
          for(const id of [...new Set([input.jobId,...(sourceJobIdHint?[sourceJobIdHint]:[])])].sort()) {
            const row=await tx.get<BuildJob>(buildJobDoc(id));
            if(row)lockedJobs.set(id,row);
          }
          const lockedCreations=new Map<string,Creation>();
          for(const id of [...new Set([input.parentId,input.forkId])].sort()) {
            const row=await tx.get<Creation>(creationDoc(id));
            if(row)lockedCreations.set(id,row);
          }
          const lockedVersions=new Map<string,CreationVersion>();
          for(const path of [...new Set([versionDoc(input.parentId,input.seedVersion),versionDoc(input.forkId,1)])].sort()) {
            const row=await tx.get<CreationVersion>(path);
            if(row)lockedVersions.set(path,row);
          }
          const lockedParent=lockedCreations.get(input.parentId);
          if (!lockedParent) throw new PlatformError('not-found', 'fork parent not found');
          const replay=lockedCreations.get(input.forkId);
          if (replay) {
            await assertForkReplay(input,forkIntent,replay,
              lockedVersions.get(versionDoc(input.forkId,1))??null,lockedJobs.get(input.jobId)??null);
            return;
          }
          if(lockedJobs.has(input.jobId)||lockedVersions.has(versionDoc(input.forkId,1))) {
            throw new PlatformError('conflict',`fork command ${input.forkId} has orphaned authority`);
          }
          assertLockedCompositionAdmissionFenceV1(lockedCompositionControl, {
            authority: forkComposition,
            fence: input.compositionAdmissionFence,
            subjectUid: input.forkerUid,
          });
          const revision = lockedParent.assetPolicyRevision ?? 0;
          if (revision !== (parent.assetPolicyRevision ?? 0)) {
            throw new PlatformError('conflict', 'parent media policy changed; reload before forking');
          }
          const policy = new AssetRejectionsRepo(this.store);
          await policy.assertInventoryAllowed(input.parentId, parent.assets, revision, tx);
          const seed=lockedVersions.get(versionDoc(input.parentId,input.seedVersion));
          if ((seed?.executionPolicy?.composition?.digest ?? null) !== (seedCompositionHint?.digest ?? null)) {
            throw new PlatformError('conflict','Composed fork predecessor admission authority changed');
          }
          if((seed?.executionPolicy?.composition?seed.creationRun?.codeJobId:undefined)!==sourceJobIdHint) {
            throw new PlatformError('conflict','Composed fork predecessor binding changed');
          }
          if (seed?.executionPolicy?.composition
            && (seed.srcPath !== input.seedSrcPath || lockedParent.activeVersion !== input.seedVersion)) {
            throw new PlatformError('conflict', 'fork requires the unchanged active predecessor');
          }
          if (seed?.executionPolicy?.composition && forkIntent) {
            throw new PlatformError('validation', 'Composed fork copies the accepted game exactly; source-edit remix intent is unsupported');
          }
          if (revision > 0 || seed?.gates?.mediaInventory?.sfx) {
            if (!seed || seed.srcPath !== input.seedSrcPath) throw new PlatformError('conflict', 'fork media requires the exact stored seed');
            await policy.assertInventoryAllowed(input.parentId, seed.assetsSnapshot, revision, tx);
            // Fork the selected version's immutable audio, never the parent's
            // newer working draft. Rejection checks below still apply first.
            if (seed.gates?.mediaInventory?.sfx) forkWrite.sfxDraft = {
              spec: seed.gates.mediaInventory.sfx, actorUid: input.forkerUid, updatedAtMs: this.now(),
            };
            await policy.assertMaterializedAllowed(input.parentId, seed.gates?.mediaInventory, revision, tx);
          }
          if (input.quota) {
            const quotaPath = creatorQuotaDoc(input.quota.uid, input.quota.day);
            const quota = await tx.get<{ count?: number }>(quotaPath);
            const count = quota?.count ?? 0;
            if (input.quota.cap <= 0 || count >= input.quota.cap) {
              throw new PlatformError('quota', `daily build limit reached (${input.quota.cap}/day)`);
            }
            await tx.set(quotaPath, {
              uid: input.quota.uid,
              day: input.quota.day,
              count: count + 1,
              updatedAt: ts,
            }, true);
          }
          const pipelineCatalog = withBuildPipelineSeeds(await tx.get<BuildPipelineCatalog>(buildPipelinesDoc()));
          await assertQualityControlRevisionV1(tx, input.qualityControlRevision);
          let composed: Awaited<ReturnType<typeof deriveCompositionLineageV1>> | undefined;
          if (seed?.executionPolicy?.composition) {
            const sourceJobId = seed.creationRun?.codeJobId;
            const sourceJob = sourceJobId ? lockedJobs.get(sourceJobId) : null;
            if (!sourceJobId || !sourceJob) throw new PlatformError('conflict', 'Composed fork predecessor job is unavailable');
            if(sourceJobId===input.jobId)throw new PlatformError('conflict','Fork target job collides with predecessor authority');
            const mediaRefs: CompositionMediaRefV1[] = [];
            const sourceReceipt = sourceJob.measuredExecution?.compositionFinalProof?.receipt;
            for (const image of sourceReceipt ? compositionReceiptItemsV1(sourceReceipt) : []) {
              if (!image.variantId) throw new PlatformError('conflict', 'Composed fork predecessor media has no immutable variant');
              const inheritedRef = seed.executionPolicy.compositionLineage?.media.kind !== 'coded'
                ? seed.executionPolicy.compositionLineage?.media.refs.find(ref => ref.slot === image.slot)
                : undefined;
              const namespace = inheritedRef?.creationId ?? input.parentId;
              const variant = await tx.get<AssetVariantDoc>(assetVariantDoc(namespace, image.variantId));
              if (!variant || variant.creationId !== namespace || variant.variantId !== image.variantId
                || variant.kind !== compositionMediaSlotKindV1(image.slot) || variant.name !== image.slot || variant.contentSha256 !== image.contentSha256
                || variant.exposureState === 'staged' || variant.reviewState === 'rejected') {
                throw new PlatformError('conflict', 'Composed fork predecessor media changed or is unavailable');
              }
              mediaRefs.push({ slot: image.slot, creationId: namespace, variantId: image.variantId,
                contentSha256: image.contentSha256, url: variant.url });
            }
            try {
              composed = await deriveCompositionLineageV1({ kind: 'fork-copy', authority: forkComposition, sourceCreationId: input.parentId,
                sourceVersion: seed, sourceJobId, sourceJob, mediaRefs });
            } catch (error) {
              throw new PlatformError('conflict', `Composed fork predecessor authority is invalid: ${error instanceof Error ? error.message : String(error)}`);
            }
          }
          const executionKind = forkIntent ? 'code-fork-remix' as const : 'source-preserving-copy' as const;
          const acceptanceMode = composed ? 'measured-first-playable' as const : input.executionPolicyMode ?? 'legacy';
          if ((acceptanceMode === 'verified-core-v2') !== !!input.qualityAdmissionV2) {
            throw new PlatformError('validation', 'verified-core-v2 fork mode and admission authority must be supplied together');
          }
          const executionPolicy = resolveBuildExecutionPolicy({
            ...(composed ? { composition: composed.authority, compositionLineage: composed.lineage } : {}),
            catalog: pipelineCatalog,
            mode: acceptanceMode,
            executionKind,
            requested: input.seedPipelineSnapshot ? buildPipelineRef(input.seedPipelineSnapshot) : undefined,
            active: input.seedPipelineSnapshot,
            qualityRun: input.qualityRun,
            qualityAdmission: input.qualityAdmissionV2?.admission,
          });
          // A composed successor never receives a second media allowance.
          const measuredExecution = composed
            ? createMeasuredBuildExecution(this.now(), compositionMeasuredLimits(compositionSuccessorMediaWindow(composed.authority)), compositionRecipeRevisionV1(composed.authority.spec.profile))
            : undefined;
          if (measuredExecution) {
            measuredExecution.compositionDigest = composed!.authority.digest;
            measuredExecution.compositionLineageDigest = composed!.lineage.digest;
          }
          const jobWrite = queuedBuildJobWrite({ jobId: input.jobId, creationId: input.forkId, version: 1,
            ...(measuredExecution ? { measuredExecution } : {}),
            ...(executionPolicy.acceptanceMode === 'verified-core' && executionKind === 'code-fork-remix'
              ? { requiredWorkerCapability: 'verified-core-v1' as const }
              : executionPolicy.acceptanceMode === 'verified-core-v2'
                ? { requiredWorkerCapability: 'verified-core-v2' as const }
                : {}) }, ts);
          await tx.set(creationDoc(input.forkId), forkWrite);
          await tx.set(slugDoc(slug), claimWrite);
          await tx.set(buildJobDoc(input.jobId), jobWrite);
          const qualityAuthority = input.qualityAdmissionV2
            ? await writeQualityAdmissionV2(tx, input.qualityAdmissionV2, {
                jobId: input.jobId,
                creationId: input.forkId,
                version: 1,
                commandId: input.jobId,
                executionKind,
                baseVersion: 0,
              }, this.now())
            : undefined;
          const versionWrite = queuedVersionWrite({
            prompt: composed ? seed!.prompt : forkIntent,
            provider: parent.engine.provider,
            model: parent.engine.model,
            diffSummary: `Forked from ${parent.slug} v${input.seedVersion}`,
            ...(!composed ? { srcPath: input.seedSrcPath } : {}),
            ...(composed ? { buildMode: 'copy' as const } : forkIntent ? { buildMode: 'fork-remix' as const } : {}),
            updatedBy: input.forkerUid,
            updatedByHandle: input.forkerHandle,
            pipelineSnapshot: executionPolicy.pipelineSnapshot,
            executionPolicy,
            ...(composed && measuredExecution ? { creationRun: {
              runId: input.jobId,
              approvedDesignRev: seed!.designRev ?? input.seedDesignRev ?? parent.designRev ?? 1,
              acceptedAt: measuredExecution.acceptedAt,
              codeJobId: input.jobId,
              visualJobId: null,
              musicJobId: null,
              proceduralSfx: 'included' as const,
            } } : {}),
            ...(input.qualityRun ? { qualityRun: input.qualityRun } : {}),
            ...(seedCapabilityLock ? { capabilityLock: seedCapabilityLock } : {}),
            ...(qualityAuthority ? {
              qualityRunId: qualityAuthority.runId,
              qualitySummary: {
                recipe: 'verified-core@2' as const,
                phase: 'contract-pending',
                proofDebtCodes: forkIntent ? [] : ['legacy-gameplay-proof-unavailable'],
              },
            } : {}),
            settingsSnapshot: composed ? seed!.settingsSnapshot : input.seedSettingsSnapshot,
            settingsSchemaVersion: composed ? seed!.settingsSchemaVersion : input.seedSettingsSchemaVersion,
            settingsRev: 1,
            settingsDiff: '',
            designSnapshot: composed ? seed!.designSnapshot : input.seedDesignSnapshot,
            designRev: composed ? seed!.designRev : input.seedDesignSnapshot ? (input.seedDesignRev ?? parent.designRev ?? 1) : undefined,
            assetsSnapshot: composed ? structuredClone(seed!.assetsSnapshot ?? []) : toVersionAssetRefs(parent.assets),
          }, 1, ts);
          await tx.set(versionDoc(input.forkId, 1), versionWrite);
        });
      } catch (e) {
        // a concurrent claim raced past the pre-read — re-mint and retry
        if (isSlugUniqueViolation(e)) continue;
        throw e;
      }
      return (await this.get(input.forkId))!;
    }
    throw new PlatformError(
      'conflict',
      `createFork: could not mint a unique slug for ${parent.slug} after ${FORK_SLUG_ATTEMPTS} attempts`,
    );
  }

  /** Fetch a creation by id, or null if absent. */
  async get(creationId: string): Promise<Creation | null> {
    return this.store.getDoc<Creation>(creationDoc(creationId));
  }

  /**
   * Count a creator's UNBUILT drafts (`status: 'draft'` with no successful
   * build) — the createCreation draft cap. Queries by `creatorUid` ONLY (a
   * single-field, always-present index) and filters `status`/`latestVersion`
   * in memory, so it needs NO composite index — unlike {@link list} with the
   * default `newest` sort, whose `creatorUid` + `createdAt` order requires one.
   */
  async countUnbuiltDrafts(creatorUid: Uid): Promise<number> {
    const rows = await this.store.query<Creation>(creationsCollection(), {
      where: [['creatorUid', '==', creatorUid]],
    });
    return rows.filter(isUnbuiltDraft).length;
  }

  /**
   * Creations with no working version yet, touched since `since`: the fresh
   * Creates an operator can look over (tools/ops/resume-stranded-creates.mjs).
   */
  async listUnfinishedSince(since: number): Promise<Array<Creation & { id: string }>> {
    return this.store.query<Creation & { id: string }>(creationsCollection(), {
      where: [['activeVersion', '==', 0], ['updatedAt', '>=', since]],
    });
  }

  /**
   * Count a creator's LLM creations (`kind: 'creation'` — seeded natives never
   * count), split by fork lineage: `{ forks: true }` counts games they FORKED
   * (`forks_created` metric — Innovator/Visionary), `{ forks: false }` their
   * ORIGINALS (`games_created` — Maker/Renaissance Person; drafts included,
   * matching the my_badge_progress view). Same single-field `creatorUid` query
   * + in-memory filter idiom as {@link countUnbuiltDrafts} (no composite
   * index, no null-comparison query semantics to lean on).
   */
  async countByCreator(creatorUid: Uid, opts: { forks: boolean }): Promise<number> {
    const rows = await this.store.query<Creation>(creationsCollection(), {
      where: [['creatorUid', '==', creatorUid]],
    });
    return rows.filter(
      (c) =>
        (c.kind ?? 'creation') === 'creation' &&
        (opts.forks ? c.forkParentId != null : c.forkParentId == null),
    ).length;
  }

  /**
   * Count a creator's creations that have EVER been an Editors' Pick — the
   * `editors_picks` badge metric. Same single-field `creatorUid` query +
   * in-memory filter idiom as {@link countByCreator} (no composite index, no
   * nested-jsonb query semantics to lean on): the `editorsPick.everSelected`
   * mark is checked in memory. The SQL twin is the `editors_picks` branch of
   * the `my_badge_progress` view.
   */
  async countEditorsPicksByCreator(creatorUid: Uid): Promise<number> {
    const rows = await this.store.query<Creation>(creationsCollection(), {
      where: [['creatorUid', '==', creatorUid]],
    });
    return rows.filter((c) => c.editorsPick?.everSelected === true).length;
  }

  /**
   * DIVERGED (public) forks of a parent, newest divergence first — the
   * `/g/:slug` forks panel (rendered lazily, so viral pages pay zero fork
   * reads until someone expands it). Draft forks are excluded here AND
   * invisible to non-members by RLS.
   */
  async listForks(parentId: string, limit = 24): Promise<Array<Creation & { id: string }>> {
    return this.store.query<Creation>(creationsCollection(), {
      where: [
        ['forkParentId', '==', parentId],
        ['visibility', '==', 'public'],
      ],
      orderBy: [['divergedAt', 'desc']],
      limit,
    });
  }

  /** Resolve a slug to its creation id via the claim doc, or null. */
  async resolveSlug(slug: string): Promise<string | null> {
    const claim = await this.store.getDoc<SlugClaim>(slugDoc(slug));
    return claim ? claim.creationId : null;
  }

  /** Fetch a creation by its slug, or null. */
  async getBySlug(slug: string): Promise<Creation | null> {
    const id = await this.resolveSlug(slug);
    return id ? this.get(id) : null;
  }

  /**
   * List creations with filters + sort. `trending` sorts in-memory by the pure
   * {@link score} (the doc store can't express the decay); `newest` orders by
   * `createdAt desc` at the query layer. `limit` clamps the RESULT (applied
   * after the trending sort so it isn't defeated by query-time truncation).
   */
  async list(filter: ListCreationsFilter = {}): Promise<Creation[]> {
    const sort = filter.sort ?? 'newest';
    const where: NonNullable<QuerySpec['where']> = [];
    if (filter.style) where.push(['style', '==', filter.style]);
    if (filter.creatorUid) where.push(['creatorUid', '==', filter.creatorUid]);
    if (filter.memberUid) where.push(['memberUids', 'array-contains', filter.memberUid]);
    if (filter.visibility) where.push(['visibility', '==', filter.visibility]);

    const spec: QuerySpec = { where };
    if (sort === 'newest') {
      spec.orderBy = [['createdAt', 'desc']];
      if (filter.limit !== undefined) spec.limit = filter.limit;
    }

    const rows = await this.store.query<Creation>(creationsCollection(), spec);

    if (sort === 'trending') {
      const now = this.now();
      rows.sort(
        (a, b) =>
          trendingScore(b.likesCount, b.viewsCount, b.updatedAt, now, b.playsCount) -
          trendingScore(a.likesCount, a.viewsCount, a.updatedAt, now, a.playsCount),
      );
    } else if (sort === 'liked') {
      rows.sort((a, b) => b.likesCount - a.likesCount || b.viewsCount - a.viewsCount);
    } else if (sort === 'played') {
      rows.sort((a, b) => b.playsCount - a.playsCount || b.viewsCount - a.viewsCount);
    }
    return filter.limit !== undefined ? rows.slice(0, filter.limit) : rows;
  }

  /**
   * Every PUBLIC creation with its doc `id` surfaced — the Editors' Pick
   * picker's working set. It owns the eligibility filter (live/playable/
   * community) and the stale-`current` cleanup itself, so this returns the raw
   * public set (natives included) rather than pre-filtering. A single-field
   * `visibility` query (no composite index), like {@link countByCreator}.
   */
  async listPublicWithIds(): Promise<Array<Creation & { id: string }>> {
    return this.store.query<Creation & { id: string }>(creationsCollection(), {
      where: [['visibility', '==', 'public']],
    });
  }

  /**
   * Update mutable creator-owned fields and bump `updatedAt`. Only the
   * whitelisted fields are written (server-owned counters/status can't be
   * authored here). Throws `PlatformError('not-found')` if the creation is gone.
   */
  async update(creationId: string, patch: UpdateCreationInput): Promise<void> {
    await this.store.update(creationDoc(creationId), this.updatePatchData(patch));
  }

  /** Publish a generated title treatment iff its immutable title snapshot is
   * still current. This CAS is the sole writer of the overlay pair and does
   * not bump updatedAt (generated presentation must not juice ranking). */
  async setTitleOverlayIfTitle(
    creationId: string,
    expectedTitle: string,
    url: string,
  ): Promise<boolean> {
    return this.store.guardedUpdate<Creation>(
      creationDoc(creationId),
      (creation) => creation !== null && creation.title === expectedTitle,
      { titleOverlayUrl: url, titleOverlayForTitle: expectedTitle },
    );
  }

  /** The whitelisted write a creator-owned metadata patch maps to, plus the
   *  `updatedAt` bump — shared by {@link update} and {@link publishFlip} so
   *  the two writers can never drift on what a patch may touch. */
  private updatePatchData(patch: UpdateCreationInput): Record<string, unknown> {
    const data: Record<string, unknown> = { updatedAt: this.store.serverTimestamp() };
    if (patch.title !== undefined) data.title = patch.title;
    if (patch.description !== undefined) data.description = patch.description;
    if (patch.style !== undefined) data.style = patch.style;
    if (patch.visibility !== undefined) data.visibility = patch.visibility;
    if (patch.concept !== undefined) data.concept = patch.concept;
    if (patch.avoidDirection !== undefined) data.avoidDirection = patch.avoidDirection;
    return data;
  }

  /**
   * The PUBLISH flip — everything the unlisted→public act writes, in ONE
   * transaction (FOR UPDATE re-read + per-path advisory lock on Postgres), so
   * a failure partway can never strand `visibility: 'public'` with
   * `publishedAt` NULL (a state the handler's flip precondition could not
   * heal) or spend the set-once stamp without moving the parent's counter:
   *
   *  1. re-read the creation under the lock and re-verify it is currently
   *     NON-public — a concurrent publish that committed first wins, and this
   *     call then applies the metadata patch only (no stamp, no count);
   *  2. write the patch (incl. `visibility: 'public'`) and — on the FIRST
   *     publish ever, `publishedAt` still empty — the set-once stamp in the
   *     SAME update. This is the ONE writer of {@link Creation.publishedAt};
   *     grandfathered rows (public, or diverged, before the gate) arrive
   *     pre-stamped by the 0051 backfill and can never re-stamp;
   *  3. when that first publish is a FORK's, count it on the parent
   *     (`forksCount` + 1) in the same transaction — the count-once credit.
   *     A missing parent is a silent skip (a deleted parent must never fail
   *     the fork's publish); no parent `updatedAt` bump (being forked is not
   *     the parent's content changing — it must not juice trending recency).
   *
   * Throws `PlatformError('not-found')` when the creation is gone.
   */
  async publishFlip(
    docTx: DocTxRunner,
    creationId: string,
    patch: UpdateCreationInput & { visibility: 'public' },
    at: number,
  ): Promise<{ flipped: boolean; firstPublish: boolean }> {
    return docTx.runTransaction(async (tx) => {
      const current = await tx.get<Creation>(creationDoc(creationId));
      if (!current) {
        throw new PlatformError('not-found', `publishFlip: creation ${creationId} not found`);
      }
      const flipped = current.visibility !== 'public';
      const firstPublish = flipped && current.publishedAt == null;
      const data = this.updatePatchData(patch);
      if (firstPublish) data.publishedAt = at;
      await tx.update(creationDoc(creationId), data);
      if (firstPublish && current.forkParentId) {
        const parent = await tx.get<Creation>(creationDoc(current.forkParentId));
        if (parent) {
          await tx.update(creationDoc(current.forkParentId), {
            forksCount: this.store.increment(1),
          });
        }
      }
      return { flipped, firstPublish };
    });
  }

  /**
   * Resolve a previously accepted command before mutable creation validation.
   * This is the fast retry path after the acceptance response was lost.
   */
  async reuseAcceptedBuildCommand(
    creationId: string,
    commandId: string,
    requestFingerprint: string,
    callerUid: string,
  ): Promise<AcceptBuildResult | null> {
    const prior = await this.store.getDoc<StoredBuildCommandLedger>(
      buildCommandLedgerPath(creationId, commandId),
    );
    if (!prior) return null;
    if (
      prior.action !== 'build.request' ||
      prior.creationId !== creationId ||
      prior.actionPerformedBy !== callerUid ||
      prior.details?.requestFingerprint !== requestFingerprint
    ) {
      throw new PlatformError(
        'conflict',
        `build command ${commandId} was already used with different authority or payload`,
      );
    }
    return acceptedBuildResult(creationId, commandId, prior);
  }

  /** The build a server-owned command already accepted, whatever payload it
   * was accepted with, or null. A run that owns its command id (a composed
   * revision's build stage) observes its own acceptance through this before
   * it would rebuild the payload from state that may have moved on since. */
  async acceptedBuildCommand(creationId: string, commandId: string): Promise<AcceptBuildResult | null> {
    const prior = await this.store.getDoc<StoredBuildCommandLedger>(buildCommandLedgerPath(creationId, commandId));
    if (!prior) return null;
    if (prior.action !== 'build.request' || prior.creationId !== creationId) {
      throw new PlatformError('conflict', `build command ${commandId} is not this creation's build request`);
    }
    return acceptedBuildResult(creationId, commandId, prior);
  }

  /**
   * Accept one creator build command as a single all-or-nothing write. The
   * deterministic ledger row is read first again under the creation lock, so
   * concurrent identical submissions converge on the same accepted result.
   */
  async acceptBuild(docTx: DocTxRunner, input: AcceptBuildInput): Promise<AcceptBuildResult> {
    return docTx.runTransaction(async (tx) => {
      const ledgerPath = buildCommandLedgerPath(input.creationId, input.commandId);
      const prior = await tx.get<StoredBuildCommandLedger>(ledgerPath);
      if (prior) {
        const details = prior.details ?? {};
        if (
          prior.action !== 'build.request' ||
          prior.creationId !== input.creationId ||
          prior.actionPerformedBy !== input.actionPerformedBy ||
          details.requestFingerprint !== input.requestFingerprint
        ) {
          throw new PlatformError(
            'conflict',
            `build command ${input.commandId} was already used with different authority or payload`,
          );
        }
        return acceptedBuildResult(input.creationId, input.commandId, prior);
      }

      if (!input.composition) throw new PlatformError('validation', GENERIC_CREATION_RETIRED);
      if (input.composition) {
        await assertCompositionAdmissionFenceV1(tx, {
          authority: input.composition,
          fence: input.compositionAdmissionFence,
          subjectUid: input.actionPerformedBy,
        });
      }

      const current = await tx.get<Creation>(creationDoc(input.creationId));
      if (!current) {
        throw new PlatformError('not-found', `acceptBuild: creation ${input.creationId} not found`);
      }
      const currentAuthority = {
        latestVersion: current.latestVersion ?? 0,
        activeVersion: current.activeVersion ?? 0,
        settingsRev: current.settingsRev ?? 0,
        designRev: current.designRev ?? 0,
        analysisRevision: current.analysisDraft?.revision ?? 0,
      };
      if (
        currentAuthority.latestVersion !== input.expectedLatestVersion ||
        currentAuthority.activeVersion !== input.expectedActiveVersion ||
        currentAuthority.settingsRev !== input.expectedSettingsRev ||
        currentAuthority.designRev !== input.expectedDesignRev
      ) {
        throw new PlatformError(
          'conflict',
          'build approval is stale; reconcile with the current design, settings, and active source',
          { details: { currentAuthority } },
        );
      }
      if (current.buildState === 'building') {
        throw new PlatformError('conflict', 'a build is already in progress for this creation');
      }

      const currentPlatformSettings = normalizePlatformSettings(
        await tx.get<Partial<PlatformSettings>>(platformSettingsDoc()),
      );
      if (
        currentPlatformSettings.revision !== input.expectedPlatformSettings.revision ||
        currentPlatformSettings.creationExperience !== input.expectedPlatformSettings.creationExperience
      ) {
        throw new PlatformError(
          'conflict',
          'creation experience settings changed while this build was being approved; review and try again',
        );
      }

      const nextVersion = currentAuthority.latestVersion + 1;
      const requestedCreationRun = input.version.creationRun;
      const requestedMediaJobs = input.mediaJobs ?? [];
      if (input.qualityAdmissionV2 && (
        requestedMediaJobs.length > 0
        || requestedCreationRun?.visualJobId
        || requestedCreationRun?.musicJobId
        || requestedCreationRun?.reusedMedia
      )) {
        throw new PlatformError('validation', 'verified-core-v2 cannot mix legacy media fan-out with exact media authority');
      }
      if (!requestedCreationRun && requestedMediaJobs.length > 0) {
        throw new PlatformError('validation', 'creation-run media jobs require version run authority');
      }
      if (requestedCreationRun) {
        const expectedDesignRev = input.version.designRev ?? currentAuthority.designRev;
        if (requestedCreationRun.codeJobId !== input.jobId || requestedCreationRun.approvedDesignRev !== expectedDesignRev) {
          throw new PlatformError('validation', 'creation-run code or design authority does not match the accepted build');
        }
        const expectedBranches = new Map<string, 'visual' | 'music'>([
          ...(requestedCreationRun.visualJobId ? [[requestedCreationRun.visualJobId, 'visual'] as const] : []),
          ...(requestedCreationRun.musicJobId ? [[requestedCreationRun.musicJobId, 'music'] as const] : []),
        ]);
        const reusedIds = new Set<string>([
          ...(requestedCreationRun.reusedMedia?.visual && requestedCreationRun.visualJobId
            ? [requestedCreationRun.visualJobId]
            : []),
          ...(requestedCreationRun.reusedMedia?.music && requestedCreationRun.musicJobId
            ? [requestedCreationRun.musicJobId]
            : []),
        ]);
        if (expectedBranches.size !== requestedMediaJobs.length + reusedIds.size) {
          throw new PlatformError('validation', 'creation-run media inventory does not match the accepted version');
        }
        for (const mediaJob of requestedMediaJobs) {
          const expectedBranch = expectedBranches.get(mediaJob.jobId);
          if (
            mediaJob.creationId !== input.creationId ||
            mediaJob.creationRun?.runId !== requestedCreationRun.runId ||
            mediaJob.creationRun?.version !== nextVersion ||
            mediaJob.creationRun?.designRev !== requestedCreationRun.approvedDesignRev ||
            mediaJob.creationRun?.branch !== expectedBranch
          ) {
            throw new PlatformError('validation', 'creation-run media job does not match the accepted version authority');
          }
        }
        for (const mediaJob of requestedMediaJobs) {
          const resumeId = mediaJob.plan?.resumeFromJobId;
          if (!resumeId) continue;
          const prior = await tx.get<AssetGenJob>(assetGenJobDoc(resumeId));
          let resumable = input.measuredExecution?.recipe.revision === 3;
          if (input.composition?.media) {
            try {
              const execution = input.composition.media.execution;
              if (!execution || !input.measuredExecution) throw new Error('Composition media execution is unavailable');
              await compositionMediaResumeJournal({
                getMediaJob: id => tx.get<AssetGenJob>(assetGenJobDoc(id)),
                getBuildJob: id => tx.get<BuildJob>(buildJobDoc(id)),
                getVersion: (creationId, version) => tx.get<CreationVersion>(versionDoc(creationId, version)),
              }, {...mediaJob, id:mediaJob.jobId, measuredBudget:{schemaVersion:1,buildJobId:input.jobId,
                acceptedAt:input.measuredExecution.acceptedAt,deadlineAt:input.measuredExecution.deadlineAt,
                role:execution.role,authorityDigest:input.composition.digest,
                executionDigest:await compositionMediaExecutionDigest(execution)}}, input.composition);
              resumable = true;
            } catch {
              resumable = false;
            }
          }
          if (!resumable || mediaJob.creationRun?.branch !== 'visual'
            || !prior || (prior.status !== 'succeeded' && prior.status !== 'failed') || prior.creationId !== input.creationId
            || prior.createdBy !== mediaJob.createdBy || prior.creationRun?.branch !== 'visual'
            || prior.creationRun.designRev !== requestedCreationRun.approvedDesignRev
            || prior.plan?.orientation !== mediaJob.plan?.orientation
            || canonicalJson(prior.plan?.assetNames ?? []) !== canonicalJson(mediaJob.plan?.assetNames ?? [])) {
            throw new PlatformError('conflict', 'saved visual milestones no longer match this repair');
          }
        }
        for (const reusedJobId of reusedIds) {
          const prior = await tx.get<AssetGenJob>(assetGenJobDoc(reusedJobId));
          const expectedBranch = expectedBranches.get(reusedJobId);
          const expectedPayload = expectedBranch === 'visual'
            ? input.reusedMediaAuthority?.visualPlan
            : input.reusedMediaAuthority?.musicBrief;
          const priorPayload = expectedBranch === 'visual' ? prior?.plan : prior?.audioBrief;
          const reuse = requestedCreationRun.reusedMedia;
          const sourceVersion = reuse?.sourceVersions
            ? (expectedBranch ? reuse.sourceVersions[expectedBranch] : undefined)
            : reuse?.sourceVersion;
          if (
            !prior ||
            prior.status === 'failed' ||
            prior.creationId !== input.creationId ||
            prior.creationRun?.designRev !== requestedCreationRun.approvedDesignRev ||
            prior.creationRun?.branch !== expectedBranch ||
            typeof sourceVersion !== 'number' || !Number.isInteger(sourceVersion) ||
            sourceVersion <= 0 || sourceVersion >= nextVersion ||
            prior.creationRun.version !== sourceVersion ||
            !expectedPayload ||
            canonicalJson(priorPayload ?? null) !== canonicalJson(expectedPayload)
          ) {
            throw new PlatformError('conflict', 'reused creation media is no longer valid for this repair');
          }
        }
      }

      const acceptedLineage = input.version.acceptedLineage;
      let acceptedBase: CreationVersion | null = null;
      if (acceptedLineage && acceptedLineage.baseVersion > 0) {
        acceptedBase = await tx.get<CreationVersion>(
          versionDoc(input.creationId, acceptedLineage.baseVersion),
        );
        if (
          !acceptedBase ||
          (acceptedBase.srcPath || '') !== acceptedLineage.sourceArchivePath ||
          (acceptedBase.gates?.srcSha256 && acceptedBase.gates.srcSha256 !== acceptedLineage.sourceArchiveDigest)
        ) {
          throw new PlatformError(
            'conflict',
            'active source archive changed while the build was being accepted',
            { details: { currentAuthority } },
          );
        }
      }

      // Capability authority is minted from the reviewed design snapshot at
      // acceptance. An unchanged source/design may preserve the exact trusted
      // base-row lock; this never consults a mutable active pointer or srcPath.
      let queuedCapabilityLock: GameplayCapabilityLockV1 | undefined;
      const requestedCapabilities = input.version.designSnapshot?.gameplay?.capabilities;
      if (requestedCapabilities) {
        const computed = await createGameplayCapabilityLock(requestedCapabilities);
        const baseCapabilities = acceptedBase?.designSnapshot?.gameplay?.capabilities;
        if (acceptedBase?.capabilityLock && baseCapabilities && canonicalJson(baseCapabilities) === canonicalJson(requestedCapabilities)) {
          const inherited = await verifyGameplayCapabilityLock(acceptedBase.capabilityLock);
          if (inherited.digest !== computed.digest) {
            throw new PlatformError('conflict', 'accepted base capability lock does not match its reviewed design');
          }
          queuedCapabilityLock = inherited;
        } else {
          queuedCapabilityLock = computed;
        }
      }

      let consumedAnalysis = current.analysisDraft;
      if (input.expectedAnalysisOperationId !== undefined) {
        if (
          !consumedAnalysis ||
          consumedAnalysis.operationId !== input.expectedAnalysisOperationId ||
          consumedAnalysis.revision !== input.expectedAnalysisRevision ||
          consumedAnalysis.status !== 'proposal' ||
          consumedAnalysis.inputs.title !== current.title.trim() ||
          consumedAnalysis.inputs.concept !== (current.concept ?? '').trim()
        ) {
          throw new PlatformError(
            'conflict',
            'the reviewed proposal is no longer fresh; return to review',
            { details: { currentAuthority } },
          );
        }
        consumedAnalysis = {
          ...consumedAnalysis,
          revision: consumedAnalysis.revision + 1,
          status: 'consumed',
          consumedVersion: nextVersion,
          updatedAt: this.now(),
        };
      }

      // Lock the catalog with acceptance: a concurrent hide/revision cannot
      // change the recipe between validation and the immutable queued write.
      const pipelineCatalog = withBuildPipelineSeeds(await tx.get<BuildPipelineCatalog>(buildPipelinesDoc()));
      await assertQualityControlRevisionV1(tx, input.qualityControlRevision);
      const active = currentAuthority.activeVersion > 0
        ? await tx.get<CreationVersion>(versionDoc(input.creationId, currentAuthority.activeVersion))
        : null;
      if (currentAuthority.latestVersion > 0 && input.pipeline?.stage === 'artwork' && (!active?.srcPath || active.buildStatus !== 'live')) {
        throw new PlatformError('validation', 'Add artwork requires a playable prototype with a source archive');
      }
      // A failed initial build already pinned its recipe. Hiding prevents new
      // selections, not retrying that game; artwork still requires live source.
      const failedInitial = currentAuthority.activeVersion === 0 && currentAuthority.latestVersion > 0
        ? await tx.get<CreationVersion>(versionDoc(input.creationId, currentAuthority.latestVersion))
        : null;
      // A composed game whose first build failed is created again exactly like
      // a fresh one (no live version, no inherited source); any other
      // composed follow-up is a lineage successor.
      const composedRetry = !!input.composition && failedInitial?.buildStatus === 'failed'
        && !!failedInitial.executionPolicy?.composition;
      const revisionBase = input.compositionRevision;
      if (!revisionBase && (active?.executionPolicy?.composition || (failedInitial?.executionPolicy?.composition && !composedRetry))) {
        throw new PlatformError('validation', 'Composed follow-up is not enabled');
      }
      if (revisionBase && (!input.composition || input.version.acceptedRecovery)) {
        throw new PlatformError('validation', 'A composed revision builds one newly accepted composed game');
      }
      if (input.measuredExecution?.compositionRevisionDigest) {
        throw new PlatformError('validation', 'Composition revision identity is stamped at acceptance');
      }
      const recovery = input.version.acceptedRecovery;
      const recoveryVersion = recovery ? await tx.get<CreationVersion>(versionDoc(input.creationId, recovery.version)) : null;
      const pinnedPipeline = recoveryVersion?.pipelineSnapshot ?? (active?.buildStatus === 'live' ? active.pipelineSnapshot
        : failedInitial?.buildStatus === 'failed' ? failedInitial.pipelineSnapshot : undefined);
      const executionKind = revisionBase
        ? 'composition-revision' as const
        : recovery
        ? 'code-recovery' as const
        : nextVersion === 1 || composedRetry
          ? 'code-create' as const
          : 'code-iterate' as const;
      const freshCreation = currentAuthority.latestVersion === 0 && currentAuthority.activeVersion === 0;
      const acceptanceMode = input.version.executionPolicyMode ?? 'legacy';
      if (freshCreation && acceptanceMode !== 'measured-first-playable') {
        throw new PlatformError('validation', 'fresh games require the complete creation recipe and shared execution budget');
      }
      if ((acceptanceMode === 'verified-core-v2') !== !!input.qualityAdmissionV2) {
        throw new PlatformError('validation', 'verified-core-v2 acceptance mode and admission authority must be supplied together');
      }
      const composition = input.composition ? await validateAcceptedCompositionV1(input.composition) : undefined;
      // A revision replaces exactly the live game it was prepared against:
      // the CAS'd active version, live, composed (the same accepted game) or
      // generic, and carries the art the one policy keeps from its published
      // media (each still-available variant read here, under the lock).
      let compositionRevision: CompositionRevisionV1 | undefined;
      if (revisionBase && composition) {
        if (revisionBase.baseVersion !== currentAuthority.activeVersion || !active || active.n !== revisionBase.baseVersion
          || active.buildStatus !== 'live' || (active.executionPolicy?.composition?.digest ?? null) !== revisionBase.baseAuthorityDigest) {
          throw new PlatformError('conflict', 'The live game changed while this revision was being prepared; reload and try again',
            { details: { currentAuthority } });
        }
        const sourceJobId = active.executionPolicy?.composition ? active.creationRun?.codeJobId : undefined;
        const sourceJob = sourceJobId ? await tx.get<BuildJob>(buildJobDoc(sourceJobId)) : null;
        try {
          compositionRevision = await deriveCompositionRevisionV1({ authority: composition, sourceCreationId: input.creationId,
            sourceVersion: active, sourceJob, readMedia: async bindings => (await Promise.all(bindings.map(async binding => {
              const variant = await tx.get<AssetVariantDoc>(assetVariantDoc(binding.creationId, binding.variantId));
              if (!variant || variant.variantId !== binding.variantId || !compositionVariantMatchesRefV1(variant, binding)
                || variant.reviewState === 'rejected' || variant.exposureState === 'staged') return [];
              const genJob = variant.genJobId ? await tx.get<AssetGenJob>(assetGenJobDoc(variant.genJobId)) : null;
              return [{ ref: { ...binding, url: variant.url }, origin: compositionMediaOriginV1(genJob), kind: variant.kind }];
            }))).flat() });
        } catch (error) {
          throw new PlatformError('conflict', `This game cannot be revised: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
      const measuredExecution = input.measuredExecution && compositionRevision
        ? { ...input.measuredExecution, compositionRevisionDigest: compositionRevision.digest }
        : input.measuredExecution;
      const compositionMediaExecution = composition?.media?.execution;
      const compositionMediaExecutionId = compositionMediaExecution
        ? await compositionMediaExecutionDigest(compositionMediaExecution)
        : undefined;
      if (composition && (!(freshCreation || composedRetry || compositionRevision)
        || composition.intent.inputDigest !== await canonicalDigest(input.version.prompt)
        || composition.intent.settingsDigest !== await canonicalDigest(input.version.settingsSnapshot ?? null)
        || composition.intent.designDigest !== await canonicalDigest(input.version.designSnapshot ?? null)
        || input.measuredExecution?.compositionDigest !== composition.digest
        || input.measuredExecution.compositionMediaExecutionDigest !== compositionMediaExecutionId
        || !isCompositionMeasuredLimits(input.measuredExecution.limits, compositionMediaExecution)
        || (composition.media ? requestedMediaJobs.length !== 1 || !requestedCreationRun?.visualJobId
          || requestedCreationRun.musicJobId || requestedCreationRun.reusedMedia
          || requestedMediaJobs[0]?.creationRun?.branch !== 'visual'
          || canonicalJson([...(requestedMediaJobs[0]?.plan?.assetNames ?? [])].sort()) !== canonicalJson([...composition.media.slots].sort())
          : requestedMediaJobs.length !== 0 || requestedCreationRun?.visualJobId || requestedCreationRun?.musicJobId))) throw new PlatformError('validation', 'Composition acceptance basis changed');
      if (!composition && input.measuredExecution?.compositionDigest) throw new PlatformError('validation', 'Composition authority cannot be removed');
      const executionPolicy = resolveBuildExecutionPolicy({
        ...(composition ? { composition } : {}),
        ...(compositionRevision ? { compositionRevision } : {}),
        catalog: pipelineCatalog,
        mode: acceptanceMode,
        executionKind,
        requested: freshCreation ? undefined : input.pipeline ?? (pinnedPipeline ? undefined : LEGACY_BUILD_PIPELINE_REF),
        active: pinnedPipeline,
        qualityRun: input.version.qualityRun,
        qualityAdmission: input.qualityAdmissionV2?.admission,
      });
      if ((executionPolicy.acceptanceMode === 'measured-first-playable') !== !!input.measuredExecution) {
        throw new PlatformError('validation', 'measured-first-playable acceptance requires one shared execution ledger');
      }
      if (input.measuredExecution && (
        input.measuredExecution.recipe.id !== 'measured-first-playable'
        || !isDurableMeasuredRevision(input.measuredExecution.recipe.revision)
        || input.measuredExecution.acceptedAt <= 0
        || input.measuredExecution.deadlineAt !== input.measuredExecution.acceptedAt + input.measuredExecution.limits.maxWallTimeMs
      )) {
        throw new PlatformError('validation', 'invalid measured build execution authority');
      }
      const pipelineSnapshot = executionPolicy.pipelineSnapshot;
      if (input.measuredExecution && input.measuredExecution.recipe.revision !== pipelineSnapshot.revision) {
        throw new PlatformError('validation', 'measured execution recipe must match the accepted pipeline');
      }
      if ((executionPolicy.acceptanceMode === 'verified-core' || executionPolicy.acceptanceMode === 'verified-core-v2')
        && requestedCreationRun?.reusedMedia
        && (requestedCreationRun.reusedMedia.visual || requestedCreationRun.reusedMedia.music)) {
        throw new PlatformError('validation', 'verified-core media cannot reuse work without the exact accepted core basis');
      }
      if (input.expectedPipelineSnapshot && canonicalJson(input.expectedPipelineSnapshot) !== canonicalJson(pipelineSnapshot)) {
        throw new PlatformError('conflict', 'build pipeline changed while media work was being approved; review and try again');
      }
      if (recovery) {
        const failed = recoveryVersion;
        let retained;
        if (recovery.measured) {
          const job = await tx.get<BuildJob>(buildJobDoc(recovery.measured.jobId));
          const selected = job?.measuredExecution?.checkpoint;
          if (!job || !selected || selected.revision !== recovery.measured.checkpointRevision
            || selected.currentCandidateId !== recovery.measured.candidateId
            || selected.candidates[selected.currentCandidateId]?.source.generation !== recovery.sourceGeneration) {
            throw new PlatformError('conflict', 'saved measured build changed while the retry was being accepted');
          }
          retained = measuredFailedBuildCheckpoint(input.creationId, recovery.version, job) ?? undefined;
          if (!retained) throw new PlatformError('conflict', 'saved measured source is unavailable');
        }
        const checkpoint = requireFailedBuildCheckpoint(input.creationId, current, failed, input.version.prompt, pipelineSnapshot, retained);
        if (!sameFailedCheckpoint(checkpoint, recovery.checkpoint)) {
          throw new PlatformError('conflict', 'saved build changed while the retry was being accepted');
        }
      }

      const quotaPath = creatorQuotaDoc(input.quota.uid, input.quota.day);
      const quota = await tx.get<{ count?: number; musicGens?: number }>(quotaPath);
      const count = quota?.count ?? 0;
      if (input.quota.cap <= 0 || count >= input.quota.cap) {
        throw new PlatformError('quota', `daily build limit reached (${input.quota.cap}/day)`);
      }

      let acceptedCreationRun = requestedCreationRun;
      let acceptedMediaJobs = requestedMediaJobs;
      const requestedMusicJobs = requestedMediaJobs.filter((job) => job.creationRun?.branch === 'music');
      const platformMusicQuotaPath = creatorQuotaDoc(PLATFORM_MUSIC_QUOTA_UID, input.quota.day);
      let platformMusicCount = 0;
      if (requestedMusicJobs.length > 0) {
        if (requestedMusicJobs.length !== 1 || !input.musicQuota || !requestedCreationRun) {
          throw new PlatformError('validation', 'automatic music requires one bounded atomic quota reservation');
        }
        const creationMusicCount = current.musicGenerationCount ?? 0;
        const creatorMusicCount = quota?.musicGens ?? 0;
        platformMusicCount = (await tx.get<{ musicGens?: number }>(platformMusicQuotaPath))?.musicGens ?? 0;
        const musicDefaultReason = creationMusicCount >= input.musicQuota.perCreationLifetime
          ? 'creation-lifetime-cap' as const
          : creatorMusicCount >= input.musicQuota.perCreatorDay
            ? 'creator-daily-cap' as const
            : platformMusicCount >= input.musicQuota.platformPerDay
              ? 'platform-daily-cap' as const
              : null;
        if (musicDefaultReason) {
          // Music is optional to game readiness. Settle a cap as an explicit,
          // member-readable default while preserving the accepted code/art
          // run; never reject the whole Build because a spend ceiling held.
          acceptedMediaJobs = requestedMediaJobs.filter((job) => job.creationRun?.branch !== 'music');
          acceptedCreationRun = {
            ...requestedCreationRun,
            musicJobId: null,
            musicDefaultReason,
          };
        }
      }
      const acceptedMusicJobs = acceptedMediaJobs.filter((job) => job.creationRun?.branch === 'music');

      if (await tx.get(buildJobDoc(input.jobId))) {
        throw new PlatformError('conflict', `build job ${input.jobId} already exists`);
      }
      if (await tx.get(versionDoc(input.creationId, nextVersion))) {
        throw new PlatformError('conflict', `version ${input.creationId}/${nextVersion} already exists`);
      }

      const ts = this.store.serverTimestamp();
      await tx.set(
        buildJobDoc(input.jobId),
        queuedBuildJobWrite(
          { jobId: input.jobId, creationId: input.creationId, version: nextVersion,
            ...(measuredExecution ? { measuredExecution } : {}),
            ...(executionPolicy.acceptanceMode === 'verified-core'
              ? { requiredWorkerCapability: 'verified-core-v1' as const }
              : executionPolicy.acceptanceMode === 'verified-core-v2'
                ? { requiredWorkerCapability: 'verified-core-v2' as const }
                : {}) },
          ts,
        ),
      );
      const qualityAuthority = input.qualityAdmissionV2
        ? await writeQualityAdmissionV2(tx, input.qualityAdmissionV2, {
            jobId: input.jobId,
            creationId: input.creationId,
            version: nextVersion,
            commandId: input.commandId,
            executionKind,
            baseVersion: currentAuthority.activeVersion,
          }, this.now())
        : undefined;

      // Callers cannot stamp arbitrary capability locks. The value above is
      // either freshly derived from the reviewed facet or the exact validated
      // accepted-base value.
      const { capabilityLock: _callerCapabilityLock, executionPolicy: _callerExecutionPolicy,
        executionPolicyMode: _executionPolicyMode, ...queuedVersionInput } = input.version;
      await tx.set(quotaPath, {
        uid: input.quota.uid,
        day: input.quota.day,
        count: count + 1,
        ...(acceptedMusicJobs.length > 0 ? { musicGens: (quota?.musicGens ?? 0) + 1 } : {}),
        updatedAt: ts,
      }, true);
      if (acceptedMusicJobs.length > 0) {
        await tx.set(platformMusicQuotaPath, {
          uid: PLATFORM_MUSIC_QUOTA_UID,
          day: input.quota.day,
          musicGens: platformMusicCount + 1,
          updatedAt: ts,
        }, true);
      }
      await tx.set(
        versionDoc(input.creationId, nextVersion),
        queuedVersionWrite({
          ...queuedVersionInput,
          pipelineSnapshot,
          executionPolicy,
          ...(requestedCreationRun ? { creationRun: acceptedCreationRun! } : {}),
          ...(queuedCapabilityLock ? { capabilityLock: queuedCapabilityLock } : {}),
          ...(qualityAuthority ? {
            qualityRunId: qualityAuthority.runId,
            qualitySummary: { recipe: 'verified-core@2' as const, phase: 'contract-pending', proofDebtCodes: [] },
          } : {}),
        }, nextVersion, ts),
      );
      const assetGenJobs = new AssetGenJobsRepo(this.store, this.now);
      for (const mediaJob of acceptedMediaJobs) {
        await assetGenJobs.enqueueInTransaction(tx, {
          ...mediaJob,
          ...(input.measuredExecution ? {
            measuredBudget: {
              schemaVersion: 1 as const,
              buildJobId: input.jobId,
              acceptedAt: input.measuredExecution.acceptedAt,
              deadlineAt: input.measuredExecution.deadlineAt,
              ...(compositionMediaExecution ? {
                role: compositionMediaExecution.role,
                authorityDigest: composition!.digest,
                executionDigest: compositionMediaExecutionId!,
              } : {}),
            },
          } : {}),
          ...(composition ? { prerequisite: { kind: 'composition-proof' as const, authorityDigest: composition.digest,
            runInputDigest: composition.intent.inputDigest, mediaBasisDigest: null, releasedAt: null } } : {}),
          ...(executionPolicy.acceptanceMode === 'verified-core' || executionPolicy.acceptanceMode === 'verified-core-v2' ? {
            prerequisite: {
              kind: 'verified-core' as const,
              runInputDigest: qualityAuthority
                ? input.qualityAdmissionV2!.admission.originalInput.sha256
                : input.version.qualityRun!.originalInputs.inputDigest,
              mediaBasisDigest: null,
              releasedAt: null,
            },
          } : {}),
        });
      }

      const creationPatch: Record<string, unknown> = {
        latestVersion: nextVersion,
        buildState: 'building',
        updatedAt: ts,
        ...(acceptedMusicJobs.length > 0 ? { musicGenerationCount: (current.musicGenerationCount ?? 0) + 1 } : {}),
        ...(input.defaultDescription ? { description: input.defaultDescription } : {}),
        ...(consumedAnalysis !== current.analysisDraft ? { analysisDraft: consumedAnalysis } : {}),
      };
      if (nextVersion === 1 && input.version.designSnapshot) {
        creationPatch.gameDesign = input.version.designSnapshot;
        creationPatch.designRev = input.version.designRev ?? currentAuthority.designRev;
      }
      await tx.update(creationDoc(input.creationId), creationPatch);

      const quotaIdentity = `${input.quota.uid}:${input.quota.day}:builds`;
      const acceptedAt = this.now();
      await tx.set(ledgerPath, {
        action: 'build.request',
        creationId: input.creationId,
        updatedBy: input.callerUid,
        updatedByHandle: input.callerHandle,
        actionPerformedBy: input.actionPerformedBy,
        actionPerformedByHandle: input.actionPerformedByHandle,
        details: {
          commandId: input.commandId,
          requestFingerprint: input.requestFingerprint,
          version: nextVersion,
          jobId: input.jobId,
          quotaIdentity,
          dispatchState: 'queued',
          acceptedBaseVersion: acceptedLineage?.baseVersion ?? 0,
          designRev: acceptedLineage?.designRev ?? input.version.designRev ?? 0,
          settingsRev: acceptedLineage?.settingsRev ?? input.version.settingsRev ?? 0,
          authorityTransition: nextVersion === 1 ? 'creator-approved' : 'creator-accepted-iteration',
          acceptedAt,
          ...(compositionRevision ? { revisedVersion: compositionRevision.predecessor.version,
            compositionRevisionDigest: compositionRevision.digest, carriedSlots: compositionRevision.carry.length } : {}),
          ...(input.auditDetails ?? {}),
        },
        createdAt: ts,
      });

      return {
        creationId: input.creationId,
        commandId: input.commandId,
        callerUid: input.actionPerformedBy,
        requestFingerprint: input.requestFingerprint,
        version: nextVersion,
        jobId: input.jobId,
        quotaIdentity,
        dispatchState: 'queued',
        acceptedAt,
        reused: false,
      };
    });
  }

  /** Atomically enqueue a zero-model asset rebind from the current live source.
   * The inventory equality guard prevents an attachment arriving between the
   * handler's eligibility check and this commit from being falsely stamped as
   * published by the new version. */
  async acceptAssetRebind(
    docTx: DocTxRunner,
    input: AcceptAssetRebindInput,
  ): Promise<AcceptAssetRebindResult> {
    return docTx.runTransaction(async (tx) => {
      const auditAction = ASSET_REBIND_AUDIT_ACTION[input.trigger];
      const prior = await tx.get<StoredBuildCommandLedger>(actionLogDoc(input.auditId));
      if (prior) {
        if (prior.action !== auditAction || prior.creationId !== input.creationId
          || prior.actionPerformedBy !== input.actionPerformedBy
          || prior.details.commandId !== input.commandId
          || prior.details.requestFingerprint !== input.requestFingerprint
          || typeof prior.details.version !== 'number' || typeof prior.details.jobId !== 'string') {
          throw new PlatformError('conflict', 'asset publish command was reused with different authority');
        }
        return {
          creationId: input.creationId,
          version: prior.details.version,
          jobId: prior.details.jobId,
          reused: true,
        };
      }
      if (!input.composition) throw new PlatformError('validation', GENERIC_CREATION_RETIRED);
      if (input.composition) {
        await assertCompositionAdmissionFenceV1(tx, {
          authority: input.composition,
          fence: input.compositionAdmissionFence,
          subjectUid: input.actionPerformedBy,
        });
      }
      const current = await tx.get<Creation>(creationDoc(input.creationId));
      if (!current) {
        throw new PlatformError('not-found', `acceptAssetRebind: creation ${input.creationId} not found`);
      }
      if (current.buildState === 'building') {
        throw new PlatformError('conflict', 'a build is already in progress for this creation');
      }
      if (
        (current.latestVersion ?? 0) !== input.expectedLatestVersion ||
        (current.activeVersion ?? 0) !== input.expectedActiveVersion
      ) {
        throw new PlatformError('conflict', 'the active game changed; review the latest asset state and try again');
      }
      const currentAssetIds = (current.assets ?? []).map((asset) => asset.variantId).sort();
      if (input.expectedAssetPolicyRevision !== undefined && (current.assetPolicyRevision ?? 0) !== input.expectedAssetPolicyRevision) {
        throw new PlatformError('conflict', 'Media selection changed; reload before publishing');
      }
      const expectedAssetIds = [...input.expectedAssetVariantIds].sort();
      if (
        currentAssetIds.length !== expectedAssetIds.length ||
        currentAssetIds.some((id, index) => id !== expectedAssetIds[index])
      ) {
        throw new PlatformError('conflict', 'the approved asset inventory changed; review it and try again');
      }

      const seed = await tx.get<CreationVersion>(
        versionDoc(input.creationId, input.expectedActiveVersion),
      );
      if (
        !seed ||
        seed.buildStatus !== 'live' ||
        !seed.srcPath ||
        seed.srcPath !== input.version.srcPath
      ) {
        throw new PlatformError('conflict', 'the active source archive is no longer publishable');
      }
      if (seed.executionPolicy?.composition && input.version.presentationEdit) {
        throw new PlatformError('validation', 'Composed rebind supports exact approved static slots only');
      }
      if (seed.executionPolicy?.composition && input.version.artCompletionOf !== input.expectedActiveVersion) {
        throw new PlatformError('conflict', 'Composed rebind base version changed');
      }
      if (seed.executionPolicy?.composition
        && (!input.composition || !await isCompositionSuccessorV1(input.composition, seed.executionPolicy.composition))) {
        throw new PlatformError('conflict', 'Composed rebind admission authority changed');
      }
      if (!seed.executionPolicy?.composition && input.composition) {
        throw new PlatformError('conflict', 'Composition admission cannot be applied to an ordinary asset rebind');
      }
      let authenticatedWorkshopAssets: CreationAsset[] | undefined;
      if (input.workshop) {
        const inventory = await new WorkshopRepo(this.store).inventory(input.creationId, input.workshop.originVersion,
          input.workshop.revision, input.callerUid, input.callerHandle, tx);
        if (JSON.stringify(inventory.map(a => a.variantId)) !== JSON.stringify(input.workshopAssets?.map(a => a.variantId))) {
          throw new PlatformError('conflict', 'Workshop choices changed; review before applying');
        }
        authenticatedWorkshopAssets = inventory;
        const state = (await tx.get<{ state: WorkshopState }>(workshopDoc(input.creationId)))!.state;
        for (const selected of state.selections) if (selected.originVersion === input.workshop.originVersion && !selected.slot.startsWith('cover:') && inventory.some(a => a.variantId === selected.variantId)) selected.snapshotVersion = input.expectedLatestVersion + 1;
        await tx.set(workshopDoc(input.creationId), { state });
        await tx.update(creationDoc(input.creationId), { assets: inventory });
      }
      const seedCapabilityLock = seed.capabilityLock
        ? await verifyGameplayCapabilityLock(seed.capabilityLock)
        : undefined;

      let composed: Awaited<ReturnType<typeof deriveCompositionLineageV1>> | undefined;
      if (seed.executionPolicy?.composition) {
        const sourceJobId = seed.creationRun?.codeJobId;
        const sourceJob = sourceJobId ? await tx.get<BuildJob>(buildJobDoc(sourceJobId)) : null;
        if (!sourceJobId || !sourceJob) throw new PlatformError('conflict', 'Composed rebind predecessor job is unavailable');
        const assets = input.version.artCompletionAssets ?? [];
        // Rebinds replace bundle media; the cover is creation gallery art.
        const slots = compositionBundleSlotsV1(seed.executionPolicy.composition.media?.slots ?? []);
        if (!assets.length || new Set(assets.map(asset => asset.name)).size !== assets.length) {
          throw new PlatformError('validation', 'Composed rebind requires distinct approved media slots');
        }
        const mediaRefs: CompositionMediaRefV1[] = [];
        for (const asset of assets) {
          if (!slots.includes(asset.name) || asset.kind !== compositionMediaSlotKindV1(asset.name) || !asset.contentSha256) {
            throw new PlatformError('validation', 'Composed rebind requires exact media slot identities');
          }
          const variant = await tx.get<AssetVariantDoc>(assetVariantDoc(input.creationId, asset.variantId));
          if (!variant || variant.creationId !== input.creationId || variant.variantId !== asset.variantId
            || variant.kind !== asset.kind || variant.name !== asset.name || variant.url !== asset.url
            || variant.contentSha256 !== asset.contentSha256 || variant.exposureState === 'staged'
            || variant.reviewState === 'rejected') {
            throw new PlatformError('conflict', 'Composed rebind approved variant changed or is unavailable');
          }
          const approvedAssets = authenticatedWorkshopAssets ?? current.assets ?? [];
          if (!approvedAssets.some(attached => attached.variantId === asset.variantId && attached.name === asset.name
            && attached.kind === asset.kind && attached.contentSha256 === asset.contentSha256
            && attached.url === asset.url)) {
            throw new PlatformError('conflict', 'Composed rebind approved variant is no longer attached');
          }
          mediaRefs.push({ slot: asset.name, creationId: input.creationId,
            variantId: asset.variantId, contentSha256: asset.contentSha256, url: asset.url });
        }
        // Slots the creator did not replace keep the live version's exact
        // final media. A rebind references only this creation's own variants,
        // so retained media inherited from a fork parent must be replaced.
        const inheritedMedia = compositionInheritedMediaV1(seed.executionPolicy);
        const retainedReceipt = sourceJob.measuredExecution?.compositionFinalProof?.receipt;
        const retained = retainedReceipt ? compositionReceiptItemsV1(retainedReceipt) : [];
        // A strip the live version deferred stays deferred unless this rebind
        // supplies it (the follow-up art pass's regenerated strip).
        const deferred = new Set((retainedReceipt?.deferred ?? []).map(item => item.slot));
        for (const slot of slots.filter(slot => !assets.some(asset => asset.name === slot) && !deferred.has(slot))) {
          const image = retained.find(row => row.slot === slot);
          const namespace = inheritedMedia.get(slot)?.creationId ?? input.creationId;
          if (!image?.variantId || namespace !== input.creationId) {
            throw new PlatformError('validation', `Composed rebind must also replace inherited media: ${slot}`);
          }
          const variant = await tx.get<AssetVariantDoc>(assetVariantDoc(input.creationId, image.variantId));
          if (!variant || variant.contentSha256 !== image.contentSha256 || variant.name !== slot) {
            throw new PlatformError('conflict', 'Composed rebind retained media changed or is unavailable');
          }
          mediaRefs.push({ slot, creationId: input.creationId, variantId: image.variantId,
            contentSha256: image.contentSha256, url: variant.url });
        }
        try {
          composed = await deriveCompositionLineageV1({ kind: 'asset-rebind', authority: input.composition!, sourceCreationId: input.creationId,
            sourceVersion: seed, sourceJobId, sourceJob, mediaRefs });
        } catch (error) {
          throw new PlatformError('conflict', `Composed rebind predecessor authority is invalid: ${error instanceof Error ? error.message : String(error)}`);
        }
      }

      const nextVersion = input.expectedLatestVersion + 1;
      if (await tx.get(buildJobDoc(input.jobId))) {
        throw new PlatformError('conflict', `build job ${input.jobId} already exists`);
      }
      if (await tx.get(versionDoc(input.creationId, nextVersion))) {
        throw new PlatformError('conflict', `version ${input.creationId}/${nextVersion} already exists`);
      }

      // The exact selected live version is the only authority for a copy
      // build's capability membership. A caller cannot inject a replacement,
      // and a legacy seed without a lock stays explicitly unclassified.
      const pipelineCatalog = withBuildPipelineSeeds(await tx.get<BuildPipelineCatalog>(buildPipelinesDoc()));
      await assertQualityControlRevisionV1(tx, input.qualityControlRevision);
      const acceptanceMode = composed ? 'measured-first-playable' as const : input.version.executionPolicyMode ?? 'legacy';
      if ((acceptanceMode === 'verified-core-v2') !== !!input.qualityAdmissionV2) {
        throw new PlatformError('validation', 'verified-core-v2 rebind mode and admission authority must be supplied together');
      }
      const executionPolicy = resolveBuildExecutionPolicy({
        ...(composed ? { composition: composed.authority, compositionLineage: composed.lineage } : {}),
        catalog: pipelineCatalog,
        mode: acceptanceMode,
        executionKind: 'creator-asset-rebind',
        requested: seed.pipelineSnapshot ? buildPipelineRef(seed.pipelineSnapshot) : undefined,
        active: seed.pipelineSnapshot,
        qualityRun: seed.qualityRun,
        qualityAdmission: input.qualityAdmissionV2?.admission,
      });
      const ts = this.store.serverTimestamp();
      // A composed successor never receives a second media allowance.
      const measuredExecution = composed
        ? createMeasuredBuildExecution(this.now(), compositionMeasuredLimits(compositionSuccessorMediaWindow(composed.authority)), compositionRecipeRevisionV1(composed.authority.spec.profile))
        : undefined;
      if (measuredExecution) {
        measuredExecution.compositionDigest = composed!.authority.digest;
        measuredExecution.compositionLineageDigest = composed!.lineage.digest;
      }
      await tx.set(
        buildJobDoc(input.jobId),
        queuedBuildJobWrite(
          { jobId: input.jobId, creationId: input.creationId, version: nextVersion,
            ...(measuredExecution ? { measuredExecution } : {}),
            ...(input.qualityAdmissionV2 ? { requiredWorkerCapability: 'verified-core-v2' as const } : {}) },
          ts,
        ),
      );
      const qualityAuthority = input.qualityAdmissionV2
        ? await writeQualityAdmissionV2(tx, input.qualityAdmissionV2, {
            jobId: input.jobId,
            creationId: input.creationId,
            version: nextVersion,
            commandId: input.auditId,
            executionKind: 'creator-asset-rebind',
            baseVersion: input.expectedActiveVersion,
          }, this.now())
        : undefined;
      const { capabilityLock: _callerCapabilityLock, executionPolicy: _callerExecutionPolicy, srcPath: _callerSrcPath,
        executionPolicyMode: _executionPolicyMode, qualityRun: _callerQualityRun, ...queuedVersionInput } = input.version;
      await tx.set(
        versionDoc(input.creationId, nextVersion),
        queuedVersionWrite({
          ...queuedVersionInput,
          ...(composed ? {
            prompt: seed.prompt,
            settingsSnapshot: seed.settingsSnapshot,
            settingsSchemaVersion: seed.settingsSchemaVersion,
            settingsRev: seed.settingsRev,
            settingsDiff: '',
            designSnapshot: seed.designSnapshot,
            designRev: seed.designRev,
            creationRun: {
              runId: input.commandId,
              approvedDesignRev: seed.designRev ?? 1,
              acceptedAt: measuredExecution!.acceptedAt,
              codeJobId: input.jobId,
              visualJobId: null,
              musicJobId: null,
              proceduralSfx: 'included' as const,
            },
          } : { srcPath: _callerSrcPath }),
          pipelineSnapshot: executionPolicy.pipelineSnapshot,
          executionPolicy,
          ...(seed.qualityRun ? { qualityRun: seed.qualityRun } : {}),
          ...(seedCapabilityLock ? { capabilityLock: seedCapabilityLock } : {}),
          ...(qualityAuthority ? {
            qualityRunId: qualityAuthority.runId,
            qualitySummary: {
              recipe: 'verified-core@2' as const,
              phase: 'contract-pending',
              proofDebtCodes: ['legacy-gameplay-proof-unavailable'],
            },
          } : {}),
        }, nextVersion, ts),
      );
      await tx.update(creationDoc(input.creationId), {
        latestVersion: nextVersion,
        buildState: 'building',
        updatedAt: ts,
      });
      await tx.set(actionLogDoc(input.auditId), {
        action: auditAction,
        creationId: input.creationId,
        updatedBy: input.callerUid,
        updatedByHandle: input.callerHandle,
        actionPerformedBy: input.actionPerformedBy,
        actionPerformedByHandle: input.actionPerformedByHandle,
        details: {
          commandId: input.commandId,
          requestFingerprint: input.requestFingerprint,
          jobId: input.jobId,
          version: nextVersion,
          seedVersion: input.expectedActiveVersion,
          assets: input.version.artCompletionAssets?.length ?? 0,
          provider: 'copy',
          model: 'copy',
          authorityTransition: input.trigger,
        },
        createdAt: ts,
      });

      return { creationId: input.creationId, version: nextVersion, jobId: input.jobId, reused: false };
    });
  }

  /**
   * Replace the ANSWERED wizard settings — the ONLY writer of `settings` /
   * `settingsSchemaVersion` / `settingsRev` after creation (the three fields
   * are server-owned in the rules; the createCreation/updateGameSettings
   * handlers gate + validate before calling). A FULL replacement, not a
   * merge: the wizard/Settings tab always submits the whole answered map.
   * Bumps `settingsRev`; does NOT bump `updatedAt` — a settings edit is
   * intent, not published content (it must not juice trending recency; the
   * build that applies it will). `template` is untouched here — the starter
   * family is stamped at creation and re-stamped only via {@link setDesign}'s
   * engineHint while unbuilt. Returns the new revision. Throws
   * `PlatformError('not-found')` when the creation is gone.
   */
  async setSettings(
    creationId: string,
    values: PartialGameSettings,
    schemaVersion: number,
    expectedRevision?: number,
  ): Promise<number> {
    const creation = await this.get(creationId);
    if (!creation) {
      throw new PlatformError('not-found', `setSettings: creation ${creationId} not found`);
    }
    const currentRevision = creation.settingsRev ?? 0;
    if (expectedRevision !== undefined && expectedRevision !== currentRevision) {
      throw new PlatformError('conflict', 'settings changed in another session', {
        details: { currentAuthority: { settingsRev: currentRevision } },
      });
    }
    const settingsRev = currentRevision + 1;
    const won = await this.store.guardedUpdate<Creation>(creationDoc(creationId), (live) =>
      live !== null && (live.settingsRev ?? 0) === currentRevision,
    {
      settings: { ...values },
      settingsSchemaVersion: schemaVersion,
      settingsRev,
    });
    if (!won) {
      throw new PlatformError('conflict', 'settings changed in another session');
    }
    return settingsRev;
  }

  /**
   * Write the creation's GameDesign head — the design analog of
   * {@link setSettings}. Bumps `designRev` and returns it. Trusted-writer
   * only (the updateGameDesign callable + the builder's iterate auto-amend);
   * the design is validated by the CALLER (validateGameDesign) — this method
   * only persists.
   *
   * `template`/`style` re-stamps (the concept-analysis engine hint) are
   * accepted ONLY while the creation is unbuilt (`latestVersion === 0`) —
   * after the first build the seed family is history, not a knob (the
   * template-lock, successor of the old genre-lock). A locked re-stamp
   * attempt throws `conflict` rather than silently dropping the field.
   */
  async setDesign(
    creationId: string,
    design: GameDesign,
    opts: {
      concept?: string;
      template?: TemplateFamily;
      style?: CreationStyle;
      expectedRevision?: number;
    } = {},
  ): Promise<number> {
    const creation = await this.get(creationId);
    if (!creation) {
      throw new PlatformError('not-found', `setDesign: creation ${creationId} not found`);
    }
    const relabel = opts.template !== undefined || opts.style !== undefined;
    if (relabel && creation.latestVersion > 0) {
      throw new PlatformError(
        'conflict',
        `setDesign: template/style are fixed after the first build (creation ${creationId} is at v${creation.latestVersion})`,
      );
    }
    const currentRevision = creation.designRev ?? 0;
    if (opts.expectedRevision !== undefined && opts.expectedRevision !== currentRevision) {
      throw new PlatformError('conflict', 'design changed in another session', {
        details: { currentAuthority: { designRev: currentRevision } },
      });
    }
    const designRev = currentRevision + 1;
    // No `updatedAt`: a design edit is INTENT, not published content, and must
    // not juice trending recency — the build that applies the design bumps it.
    // Matches setSettings (its stated design analog).
    const data: Record<string, unknown> = {
      gameDesign: design,
      designRev,
    };
    if (opts.concept !== undefined) data.concept = opts.concept;
    if (opts.template !== undefined) data.template = opts.template;
    if (opts.style !== undefined) data.style = opts.style;
    const won = await this.store.guardedUpdate<Creation>(
      creationDoc(creationId),
      (live) =>
        live !== null &&
        (live.designRev ?? 0) === currentRevision &&
        (!relabel || live.latestVersion === 0),
      data,
    );
    if (!won) {
      throw new PlatformError('conflict', 'design changed or the scaffold locked in another session');
    }
    return designRev;
  }

  /**
   * Persist the concept-analysis pass's fetched reference briefs (write-once
   * in practice — analysis runs once per creation; a re-analysis overwrites).
   * Trusted-writer only.
   */
  async setReferenceBriefs(creationId: string, briefs: ReferenceBrief[]): Promise<void> {
    await this.store.update(creationDoc(creationId), { referenceBriefs: briefs });
  }

  /** Read the current durable analysis operation, if this creation predates it. */
  async getAnalysisDraft(creationId: string): Promise<ConceptAnalysisDraft | null> {
    return (await this.get(creationId))?.analysisDraft ?? null;
  }

  /** Save a bounded incomplete manual draft with source + revision CAS. */
  async saveConceptManualDraft(
    creationId: string,
    input: SaveConceptManualDraftInput,
  ): Promise<ConceptManualDesignDraft> {
    const creation = await this.get(creationId);
    if (!creation) throw new PlatformError('not-found', `creation ${creationId} not found`);
    const sourceRevision = creation.analysisDraft?.revision ?? 0;
    const currentRevision = creation.conceptManualDraft?.revision ?? 0;
    if (input.sourceRevision !== sourceRevision || input.expectedRevision !== currentRevision) {
      throw new PlatformError('conflict', 'manual design draft changed in another session');
    }
    if (input.title !== creation.title || input.concept !== (creation.concept ?? '')) {
      throw new PlatformError('conflict', 'manual design draft source changed');
    }
    const serialized = JSON.stringify(input.design);
    if (serialized.length > 32_000) {
      throw new PlatformError('validation', 'manual design draft exceeds 32000 bytes');
    }
    const allowed = new Set([
      'summary', 'coreVerbs', 'mechanics', 'entities', 'controls', 'winCondition',
      'modeNotes', 'theme', 'artDirection', 'scenery', 'chapters', 'onboarding', 'platformer',
      'audio', 'gameplay', 'presentation',
    ]);
    if (Object.keys(input.design).some((key) => !allowed.has(key))) {
      throw new PlatformError('validation', 'manual design draft contains an unknown field');
    }
    if (
      input.unresolvedFields.length > 20 ||
      input.unresolvedFields.some((field) => !/^[a-z][a-zA-Z0-9.\[\]-]{0,79}$/.test(field))
    ) {
      throw new PlatformError('validation', 'manual design draft unresolved fields are invalid');
    }
    const draft: ConceptManualDesignDraft = {
      contractVersion: 1,
      revision: currentRevision + 1,
      sourceRevision,
      title: creation.title,
      concept: creation.concept ?? '',
      design: JSON.parse(serialized) as Partial<GameDesign>,
      unresolvedFields: [...new Set(input.unresolvedFields)],
      updatedAt: this.now(),
    };
    const won = await this.store.guardedUpdate<Creation>(
      creationDoc(creationId),
      (live) =>
        live !== null &&
        (live.analysisDraft?.revision ?? 0) === sourceRevision &&
        (live.conceptManualDraft?.revision ?? 0) === currentRevision &&
        live.title === input.title &&
        (live.concept ?? '') === input.concept,
      { conceptManualDraft: draft },
    );
    if (!won) throw new PlatformError('conflict', 'manual design draft changed in another session');
    return draft;
  }

  /** Promote only a canonically valid manual draft to GameDesign authority. */
  async promoteConceptManualDraft(
    creationId: string,
    expectedRevision: number,
    opts: { template?: TemplateFamily; style?: CreationStyle } = {},
  ): Promise<{ draft: ConceptManualDesignDraft; design: GameDesign; designRev: number }> {
    const creation = await this.get(creationId);
    if (!creation) throw new PlatformError('not-found', `creation ${creationId} not found`);
    const current = creation.conceptManualDraft;
    if (!current || current.revision !== expectedRevision) {
      throw new PlatformError('conflict', 'manual design draft changed in another session');
    }
    if ((creation.analysisDraft?.revision ?? 0) !== current.sourceRevision) {
      throw new PlatformError('conflict', 'manual design draft source changed');
    }
    const design = validateGameDesign(current.design);
    const relabel = opts.template !== undefined || opts.style !== undefined;
    if (relabel && creation.latestVersion !== 0) {
      throw new PlatformError('conflict', 'the starter and style are fixed after the first build');
    }
    const designRev = (creation.designRev ?? 0) + 1;
    const promoted: ConceptManualDesignDraft = {
      ...current,
      revision: current.revision + 1,
      unresolvedFields: [],
      updatedAt: this.now(),
    };
    const won = await this.store.guardedUpdate<Creation>(
      creationDoc(creationId),
      (live) =>
        live !== null &&
        (live.analysisDraft?.revision ?? 0) === current.sourceRevision &&
        live.conceptManualDraft?.revision === current.revision &&
        (live.designRev ?? 0) === (creation.designRev ?? 0) &&
        (!relabel || live.latestVersion === 0),
      {
        conceptManualDraft: promoted,
        gameDesign: design,
        designRev,
        ...(opts.template !== undefined ? { template: opts.template } : {}),
        ...(opts.style !== undefined ? { style: opts.style } : {}),
      },
    );
    if (!won) throw new PlatformError('conflict', 'manual design draft changed in another session');
    return { draft: promoted, design, designRev };
  }

  /**
   * Claim or resume one canonical concept-analysis operation. This is a CAS on
   * the creation doc because the source concept/title and draft must be pinned
   * together: two identical callers cannot both become model/quota owners, and
   * an edit between the handler read and claim returns `input-changed`.
   */
  async claimAnalysis(
    creationId: string,
    input: ClaimConceptAnalysisInput,
  ): Promise<ClaimConceptAnalysisResult> {
    for (let retry = 0; retry < ANALYSIS_CAS_RETRIES; retry += 1) {
      const creation = await this.get(creationId);
      if (!creation) {
        throw new PlatformError('not-found', `claimAnalysis: creation ${creationId} not found`);
      }
      if (!analysisInputsCurrent(creation, input.inputs)) return { kind: 'input-changed' };

      const now = this.now();
      const current = creation.analysisDraft;
      const sameInput = current?.inputs.fingerprint === input.inputs.fingerprint;
      const incomingAnswers = Object.entries(input.answers ?? {});
      // The creator's own answers win over Deej's, even once Deej's are being analyzed.
      const replacesDeej = sameInput && current?.status === 'claimed' && current.answeredForCreator !== undefined
        && !input.answeredForCreator && incomingAnswers.some(([id, answer]) => current.partialAnswers[id] !== answer);
      const pendingQuestions = current?.status === 'clarification' && current.result?.kind === 'clarification'
        ? current.result.questions
        : replacesDeej ? current!.answeredForCreator!.questions : null;
      if (incomingAnswers.length > 0) {
        if (!sameInput || !current) {
          throw new PlatformError('validation', 'analysis answers do not belong to the current concept');
        }
        if (pendingQuestions) {
          const questions = new Map(pendingQuestions.map((question) => [question.id, question]));
          for (const [id, answer] of incomingAnswers) {
            const question = questions.get(id);
            if (
              !question ||
              (question.answerMode !== 'text' && !question.options.some((option) => option.id === answer))
            ) {
              throw new PlatformError('validation', `analysis answer ${id} is not a pending option`);
            }
          }
        } else if (
          incomingAnswers.some(([id, answer]) => current.partialAnswers[id] !== answer)
        ) {
          throw new PlatformError('validation', 'analysis answers require pending clarification');
        }
      }
      const answers = { ...(current?.partialAnswers ?? {}), ...(input.answers ?? {}) };
      const hasNewAnswer =
        sameInput &&
        current?.status === 'clarification' &&
        Object.entries(input.answers ?? {}).some(([key, value]) => current.partialAnswers[key] !== value);
      const deejQuestions = input.answeredForCreator && current?.result?.kind === 'clarification' ? current.result.questions : null;

      if (sameInput && current) {
        if (current.status === 'proposal') {
          return { kind: 'reused', draft: current };
        }
        if (current.status === 'consumed') {
          return { kind: 'reused', draft: current };
        }
        if (current.status === 'clarification' && !hasNewAnswer) {
          return { kind: 'reused', draft: current };
        }
        if (
          current.status === 'claimed' &&
          current.leaseExpiresAt !== null &&
          current.leaseExpiresAt > now &&
          !replacesDeej
        ) {
          return { kind: 'busy', draft: current };
        }
        if (current.status === 'failed' && current.failure?.retryable === false) {
          return { kind: 'reused', draft: current };
        }
      }

      const startsNewOperation = !sameInput || !current;
      const staleOperations = [...(current?.staleOperations ?? [])];
      if (startsNewOperation && current) {
        staleOperations.push({
          operationId: current.operationId,
          fingerprint: current.inputs.fingerprint,
          status: 'stale',
          staleAt: now,
        });
      }
      const draft: ConceptAnalysisDraft = {
        operationId: startsNewOperation ? input.operationId : current!.operationId,
        revision: (current?.revision ?? 0) + 1,
        status: 'claimed',
        inputs: input.inputs,
        claimantId: input.claimantId,
        leaseExpiresAt: now + input.leaseMs,
        attempt: startsNewOperation ? 1 : current!.attempt + 1,
        questionCycle:
          sameInput && current?.status === 'clarification' && hasNewAnswer
            ? Math.min(
                CONCEPT_ANALYSIS_MAX_CLARIFICATION_CYCLES,
                current.questionCycle + 1,
              )
            : (current?.questionCycle ?? 0),
        partialAnswers: startsNewOperation ? { ...(input.answers ?? {}) } : answers,
        ...(deejQuestions ? { answeredForCreator: { questions: deejQuestions } } : {}),
        staleOperations: staleOperations.slice(-ANALYSIS_STALE_HISTORY_MAX),
        createdAt: startsNewOperation ? now : current!.createdAt,
        updatedAt: now,
        progress: {
          stage: 'analyzing',
          updatedAt: now,
          completedArtifacts: sameInput ? [...(current?.progress?.completedArtifacts ?? [])] : [],
        },
        ...(sameInput && current?.quotaReservedAt !== undefined
          ? { quotaReservedAt: current.quotaReservedAt }
          : {}),
        ...(sameInput && current?.quotaConsumedAt !== undefined
          ? { quotaConsumedAt: current.quotaConsumedAt }
          : {}),
      };
      const expectedSig = analysisSig(current);
      const won = await this.store.guardedUpdate<Creation>(
        creationDoc(creationId),
        (live) =>
          analysisInputsCurrent(live, input.inputs) &&
          analysisSig(live?.analysisDraft) === expectedSig,
        { analysisDraft: draft },
      );
      if (won) return { kind: 'claimed', draft };
    }
    throw new PlatformError('conflict', `claimAnalysis: contention on creation ${creationId}`);
  }

  /** Charge concept quota and mark this operation consumed in one transaction.
   * Recovery can safely reuse the marker; there is no crash window where a
   * reservation exists without the corresponding counter increment. */
  async consumeAnalysisQuota(
    docTx: DocTxRunner,
    creationId: string,
    token: ConceptAnalysisClaimToken,
    quota: { uid: Uid; day: string; cap: number },
  ): Promise<ConsumeAnalysisQuotaResult> {
    return docTx.runTransaction(async (tx) => {
      const creation = await tx.get<Creation>(creationDoc(creationId));
      const current = creation?.analysisDraft;
      if (
        !current ||
        current.status !== 'claimed' ||
        current.operationId !== token.operationId ||
        current.claimantId !== token.claimantId ||
        current.attempt !== token.attempt
      ) {
        return { kind: 'lost' };
      }
      if (current.quotaConsumedAt != null) return { kind: 'granted', draft: current };

      const quotaPath = creatorQuotaDoc(quota.uid, quota.day);
      const quotaDoc = await tx.get<{ conceptGens?: number }>(quotaPath);
      const count = quotaDoc?.conceptGens ?? 0;
      if (quota.cap <= 0 || count >= quota.cap) return { kind: 'denied', draft: current };

      const now = this.now();
      const consumed: ConceptAnalysisDraft = {
        ...current,
        revision: current.revision + 1,
        quotaReservedAt: current.quotaReservedAt ?? now,
        quotaConsumedAt: now,
        updatedAt: now,
      };
      await tx.set(quotaPath, {
        uid: quota.uid,
        day: quota.day,
        conceptGens: count + 1,
        updatedAt: this.store.serverTimestamp(),
      }, true);
      await tx.update(creationDoc(creationId), { analysisDraft: consumed });
      return { kind: 'granted', draft: consumed };
    });
  }

  /** Complete only the claim attempt that owns the lease. Reference artifacts
   * land in the same CAS as the result, so a late/reaped worker cannot replace
   * either the proposal or its evidence. */
  async completeAnalysis(
    creationId: string,
    input: CompleteConceptAnalysisInput,
  ): Promise<ConceptAnalysisDraft | null> {
    const current = await this.getAnalysisDraft(creationId);
    if (
      !current ||
      current.status !== 'claimed' ||
      current.operationId !== input.operationId ||
      current.claimantId !== input.claimantId ||
      current.attempt !== input.attempt
    ) {
      return null;
    }
    const completedAt = this.now();
    const completed: ConceptAnalysisDraft = {
      ...current,
      revision: current.revision + 1,
      status: input.result.kind === 'failure' ? 'failed' : input.result.kind,
      claimantId: null,
      leaseExpiresAt: null,
      updatedAt: completedAt,
      progress: {
        stage: input.result.kind === 'proposal'
          ? 'ready'
          : input.result.kind === 'clarification' ? 'clarification' : 'failed',
        updatedAt: completedAt,
        completedArtifacts: analysisArtifactSummaries(input.result, input.referenceBriefs, input.videoBriefs),
      },
      result: input.result,
      referenceBriefs: input.referenceBriefs,
      videoBriefs: input.videoBriefs,
      evidenceDigests: input.evidenceDigests,
      failure: undefined,
    };
    const expectedSig = analysisSig(current);
    const won = await this.store.guardedUpdate<Creation>(
      creationDoc(creationId),
      (live) =>
        analysisSig(live?.analysisDraft) === expectedSig && analysisInputsCurrent(live, current.inputs),
      {
        analysisDraft: completed,
        referenceBriefs: input.referenceBriefs,
        videoBriefs: input.videoBriefs,
      },
    );
    if (won) return completed;

    // A creator edit may race the external analyzer after the claim. Convert
    // that still-owned claim to an explicit stale state; never let the old
    // result or its reference artifacts land on the edited concept.
    const live = await this.get(creationId);
    if (
      live?.analysisDraft &&
      analysisSig(live.analysisDraft) === expectedSig &&
      (live.title.trim() !== current.inputs.title ||
        (current.inputs.settingsRevision !== undefined && (live.settingsRev ?? 0) !== current.inputs.settingsRevision) ||
        (live.concept ?? '').trim() !== current.inputs.concept)
    ) {
      const stale: ConceptAnalysisDraft = {
        ...live.analysisDraft,
        revision: live.analysisDraft.revision + 1,
        status: 'stale',
        claimantId: null,
        leaseExpiresAt: null,
        updatedAt: this.now(),
        progress: {
          stage: 'stale',
          updatedAt: this.now(),
          completedArtifacts: [...(live.analysisDraft.progress?.completedArtifacts ?? [])],
        },
      };
      await this.store.guardedUpdate<Creation>(
        creationDoc(creationId),
        (latest) =>
          analysisSig(latest?.analysisDraft) === expectedSig &&
          (latest?.title.trim() !== current.inputs.title ||
            (current.inputs.settingsRevision !== undefined && (latest?.settingsRev ?? 0) !== current.inputs.settingsRevision) ||
            (latest?.concept ?? '').trim() !== current.inputs.concept),
        { analysisDraft: stale },
      );
    }
    return null;
  }

  /** Publish only completed, member-safe artifacts for the claim that still
   * owns the attempt. Revision-guarded callbacks survive refresh while stale
   * workers cannot overwrite a retry. */
  async updateAnalysisProgress(
    creationId: string,
    token: ConceptAnalysisClaimToken,
    referenceBriefs: readonly ReferenceBrief[],
    videoBriefs: readonly VideoBrief[],
  ): Promise<ConceptAnalysisDraft | null> {
    for (let attempt = 0; attempt < ANALYSIS_CAS_RETRIES; attempt += 1) {
      const current = await this.getAnalysisDraft(creationId);
      if (
        !current || current.status !== 'claimed' ||
        current.operationId !== token.operationId ||
        current.claimantId !== token.claimantId ||
        current.attempt !== token.attempt
      ) return null;
      const updatedAt = this.now();
      const next: ConceptAnalysisDraft = {
        ...current,
        revision: current.revision + 1,
        updatedAt,
        progress: {
          stage: 'analyzing',
          updatedAt,
          completedArtifacts: analysisArtifactSummaries(undefined, referenceBriefs, videoBriefs),
        },
        referenceBriefs: [...referenceBriefs],
        videoBriefs: [...videoBriefs],
      };
      const expectedSig = analysisSig(current);
      const won = await this.store.guardedUpdate<Creation>(
        creationDoc(creationId),
        (live) =>
          analysisSig(live?.analysisDraft) === expectedSig && analysisInputsCurrent(live, current.inputs),
        {
          analysisDraft: next,
          referenceBriefs: [...referenceBriefs],
          videoBriefs: [...videoBriefs],
        },
      );
      if (won) return next;
    }
    throw new PlatformError('conflict', `analysis progress contention on creation ${creationId}`);
  }

  async failAnalysis(
    creationId: string,
    token: ConceptAnalysisClaimToken,
    failure: ConceptAnalysisFailure,
    opts: { releaseQuotaReservation?: boolean } = {},
  ): Promise<ConceptAnalysisDraft | null> {
    return this.transitionAnalysis(creationId, token, (current) => {
      const now = this.now();
      return {
        ...current,
        revision: current.revision + 1,
        status: 'failed',
        claimantId: null,
        leaseExpiresAt: null,
        updatedAt: now,
        progress: {
          stage: 'failed',
          updatedAt: now,
          completedArtifacts: [...(current.progress?.completedArtifacts ?? [])],
        },
        failure,
        ...(opts.releaseQuotaReservation ? { quotaReservedAt: null } : {}),
      };
    });
  }

  /** Build's later approval transaction can consume exactly the proposal it
   * reviewed. Clarifications and stale/failed claims are deliberately refused. */
  async consumeAnalysis(
    creationId: string,
    operationId: string,
    consumedVersion: number,
  ): Promise<ConceptAnalysisDraft | null> {
    const current = await this.getAnalysisDraft(creationId);
    if (!current || current.operationId !== operationId || current.status !== 'proposal') return null;
    const expectedSig = analysisSig(current);
    const consumed: ConceptAnalysisDraft = {
      ...current,
      revision: current.revision + 1,
      status: 'consumed',
      consumedVersion,
      updatedAt: this.now(),
    };
    const won = await this.store.guardedUpdate<Creation>(
      creationDoc(creationId),
      (live) => analysisSig(live?.analysisDraft) === expectedSig,
      { analysisDraft: consumed },
    );
    return won ? consumed : null;
  }

  private async transitionAnalysis(
    creationId: string,
    token: ConceptAnalysisClaimToken,
    transition: (current: ConceptAnalysisDraft) => ConceptAnalysisDraft,
  ): Promise<ConceptAnalysisDraft | null> {
    const current = await this.getAnalysisDraft(creationId);
    if (
      !current ||
      current.status !== 'claimed' ||
      current.operationId !== token.operationId ||
      current.claimantId !== token.claimantId ||
      current.attempt !== token.attempt
    ) {
      return null;
    }
    const expectedSig = analysisSig(current);
    const next = transition(current);
    const won = await this.store.guardedUpdate<Creation>(
      creationDoc(creationId),
      (live) => analysisSig(live?.analysisDraft) === expectedSig,
      { analysisDraft: next },
    );
    return won ? next : null;
  }

  /**
   * Persist the reference-video watch reports. Unlike the page briefs this is
   * a FULL REPLACEMENT of an accumulating list: analysis writes the concept's
   * watches, and an iteration that names a NEW reference re-writes the list
   * with the old entries plus the new one (the caller composes it — one
   * writer, one shape). Trusted-writer only.
   */
  async setVideoBriefs(creationId: string, briefs: VideoBrief[]): Promise<void> {
    await this.store.update(creationDoc(creationId), { videoBriefs: briefs });
  }

  /**
   * Stamp the cheap-LLM-drafted studio-UX example prompts — the editable
   * starting points the portal prefills into the Iterate and Assets boxes (see
   * {@link Creation.suggestedIteratePrompt} / {@link Creation.suggestedAssetPrompt}).
   * Regenerated once per successful build (create + each iterate) and backfilled
   * once for pre-existing creations; a full replacement of both fields, not a
   * merge. Trusted-writer only (the builder's finalize pass + the backfill).
   *
   * A partial `store.update` (SQL `UPDATE` on the already-live creation row) —
   * NEVER a `mergeDoc` upsert, whose proposed-INSERT NOT-NULL check would 23502
   * on the omitted columns. Does NOT bump `updatedAt`: a drafted suggestion is
   * enrichment, not published content, so it must not juice trending recency
   * (matches {@link setDesign}/{@link setEditorsPick}).
   */
  async setSuggestedPrompts(
    creationId: string,
    prompts: { suggestedIteratePrompt: string; suggestedAssetPrompt: string },
  ): Promise<void> {
    await this.store.update(creationDoc(creationId), {
      suggestedIteratePrompt: prompts.suggestedIteratePrompt,
      suggestedAssetPrompt: prompts.suggestedAssetPrompt,
    });
  }

  /**
   * Write the creation's server-owned {@link CreationEditorsPick} marks — the
   * ONLY writer of `editorsPick` (the pickEditorsPicks function gates on
   * superadmin/cron and computes the accumulated value: `everSelected` sticks,
   * `selectionCount`/`editions` accrue, `current`/`currentRank` rotate). A FULL
   * replacement of the field, not a merge — the caller passes the complete
   * next value. Does NOT bump `updatedAt`: an editorial selection is curation,
   * not creator content, so it must not juice trending recency (matches
   * {@link setEditors}/{@link setDesign}). Throws `not-found` if the creation is
   * gone.
   */
  async setEditorsPick(creationId: string, editorsPick: CreationEditorsPick): Promise<void> {
    const creation = await this.get(creationId);
    if (!creation) {
      throw new PlatformError('not-found', `setEditorsPick: creation ${creationId} not found`);
    }
    await this.store.update(creationDoc(creationId), { editorsPick });
  }

  /** Atomically attach private repair source only while this attempt still owns the build. */
  async finishFailedBuild(
    docTx: DocTxRunner,
    input: {
      creationId: string; version: number; jobId: string; workerId: string; attempt: number;
      /** Required for composed failures. It fences the exact active lease,
       * including failures before the version leaves queued admission. */
      claim?: BuildClaimAuthority;
      patch: Partial<Pick<CreationVersion, 'tokenUsage' | 'costUsd' | 'assetGen' | 'assetsSnapshot' | 'traceGcsUri' | 'debugBundleGcsUri'>> & { gates: Partial<VersionGates> };
    },
  ): Promise<boolean> {
    return docTx.runTransaction(async tx => {
      // Publication locks job → creation → version. Failure follows the same
      // order so the two terminal paths cannot deadlock each other.
      const job = await tx.get<BuildJob>(buildJobDoc(input.jobId));
      const current = await tx.get<Creation>(creationDoc(input.creationId));
      const version = await tx.get<CreationVersion>(versionDoc(input.creationId, input.version));
      const composed = !!version?.executionPolicy?.composition || !!job?.measuredExecution?.compositionDigest;
      const ownsComposedClaim = composed && !!input.claim?.claimToken
        && job?.status === 'claimed' && job.workerId === input.workerId && job.workerId === input.claim.workerId
        && job.claimToken === input.claim.claimToken && job.leaseGeneration === input.claim.leaseGeneration
        && (job.leaseExpiry ?? 0) > this.now();
      const ownsLegacyAttempt = !composed && !input.claim && job?.status === 'claimed'
        && job.workerId === input.workerId && job.attempts === input.attempt;
      const ownsCreationHead = current?.latestVersion === input.version && current.buildState === 'building';
      if (!current || !version || !job || (!ownsComposedClaim && !ownsLegacyAttempt) ||
          job.creationId !== input.creationId || job.version !== input.version ||
          (composed && version.creationRun?.codeJobId !== input.jobId) ||
          (!composed && !ownsCreationHead) ||
          !(['generating', 'gating', 'publishing'].includes(version.buildStatus)
            || composed && ownsComposedClaim && version.buildStatus === 'queued')) return false;
      const ts = this.store.serverTimestamp();
      await tx.update(versionDoc(input.creationId, input.version), {
        ...input.patch, gates: { ...version.gates, ...input.patch.gates }, buildStatus: 'failed', finishedAt: ts,
      });
      if (ownsCreationHead) {
        await tx.update(creationDoc(input.creationId), {
          buildState: 'idle', status: current.activeVersion > 0 && current.activeBundleUrl ? 'live' : 'failed', updatedAt: ts,
        });
      }
      await tx.update(buildJobDoc(input.jobId), { status: 'failed', error: (input.patch.gates.notes ?? '').slice(0, 500),
        ...(composed ? { claimToken: null, leaseExpiry: null } : {}), finishedAt: ts });
      return true;
    });
  }

  /**
   * Write the SERVER-OWNED build outcome of a creation — the fields the builder
   * worker owns and a creator may never author. Always returns
   * {@link Creation.buildState} to `'idle'` (the build is over, however it
   * ended). Lifecycle `status` is PURE: a FAILED build only fails the creation
   * when NOTHING is playable (first build never succeeded) — an iterating game
   * with a live active version stays `live`, so it never drops out of the
   * gallery over a failed iteration. Bumps `updatedAt`. Only defined fields are
   * written.
   *
   * Trusted-writer only: the Firestore rules keep `status`/`buildState`/counters
   * server-only, so this runs from the builder (admin) or a function, never the
   * client.
   */
  async setBuildOutcome(
    creationId: string,
    outcome: {
      status: 'live' | 'failed';
      latestVersion?: number;
      latestBundleUrl?: string;
      posterUrl?: string | null;
      /**
       * Does this build earn the ACTIVE (played) pointer? Required on a live
       * outcome so every caller states the intent — there is no default that
       * quietly promotes a build nobody vouched for.
       *
       * `false` publishes the version as the newest build while players keep
       * the one they had. The case it exists for: an iterate that broke a
       * working game. The creator is not blocked (their version is live and
       * they can set it active), but a regression does not reach players by
       * default.
       */
      activate?: boolean;
    },
  ): Promise<void> {
    const data: Record<string, unknown> = {
      buildState: 'idle',
      updatedAt: this.store.serverTimestamp(),
    };
    if (outcome.latestVersion !== undefined) data.latestVersion = outcome.latestVersion;
    if (outcome.latestBundleUrl !== undefined) data.latestBundleUrl = outcome.latestBundleUrl;
    const policyHead = outcome.status === 'live' ? await this.get(creationId) : null;
    if (outcome.status === 'live') {
      const targetVersion = outcome.latestVersion ?? policyHead?.latestVersion;
      const privateTarget = targetVersion === undefined ? undefined
        : await this.store.getDoc<CreationVersion>(versionDoc(creationId, targetVersion));
      const inventory = outcome.latestVersion === undefined ? undefined : privateTarget;
      if (inventory?.executionPolicy?.composition || privateTarget?.executionPolicy?.compositionV2 || privateTarget?.pipelineSnapshot?.id === 'composition-private-v2' || privateTarget?.executionPolicy?.pipelineSnapshot?.id === 'composition-private-v2') {
        throw new PlatformError('conflict', 'Composed publication must atomically activate with its claim');
      }
      await new AssetRejectionsRepo(this.store).assertInventoryAllowed(creationId, inventory?.assetsSnapshot,
        policyHead?.assetPolicyRevision ?? 0);
      await new AssetRejectionsRepo(this.store).assertMaterializedAllowed(creationId, inventory?.gates?.mediaInventory,
        policyHead?.assetPolicyRevision ?? 0);
      await new AssetRejectionsRepo(this.store).assertSfxCurrent(creationId, inventory?.gates?.mediaInventory);
    }
    if (outcome.status === 'live') {
      data.status = 'live';
      // Active follows latest — unless the caller withheld it, in which case the
      // version publishes and players keep the build they had.
      if (outcome.activate !== false) {
        if (outcome.latestVersion !== undefined) {
          const target = await this.store.getDoc<CreationVersion>(versionDoc(creationId, outcome.latestVersion));
          data.previewUrl = target?.previewUrl ?? null;
          data.posterUrl = outcome.posterUrl ?? target?.posterUrl ?? null;
        }
        if (outcome.latestVersion !== undefined) data.activeVersion = outcome.latestVersion;
        if (outcome.latestBundleUrl !== undefined) data.activeBundleUrl = outcome.latestBundleUrl;
        if (outcome.posterUrl !== undefined) data.posterUrl = outcome.posterUrl;
      }
    } else {
      // Failed iteration: heal to `live` if an earlier version is still playable,
      // else the creation truly failed (first build never produced a bundle).
      const current = await this.get(creationId);
      const playable = (current?.activeVersion ?? 0) > 0 && !!current?.activeBundleUrl;
      data.status = playable ? 'live' : 'failed';
    }
    if (outcome.status === 'live') {
      const committed = await this.store.guardedUpdate<Creation>(creationDoc(creationId),
        current => current !== null && (current.assetPolicyRevision ?? 0) === (policyHead?.assetPolicyRevision ?? 0), data);
      if (!committed) throw new PlatformError('conflict', 'asset rejection policy changed before build publication');
    } else {
      await this.store.update(creationDoc(creationId), data);
    }
  }

  /** Finalize an accepted build while re-checking its frozen base under the
   * creation lock. A result based on a superseded source is published but can
   * never steal the active pointer from an explicit rollback/roll-forward. */
  async setAcceptedBuildOutcome(
    docTx: DocTxRunner,
    creationId: string,
    outcome: {
      latestVersion: number;
      latestBundleUrl: string;
      activate: boolean;
      acceptedBaseVersion: number;
    },
  ): Promise<{ activated: boolean; superseded: boolean }> {
    return docTx.runTransaction(async (tx) => {
      const current = await tx.get<Creation>(creationDoc(creationId));
      if (!current) throw new PlatformError('not-found', `creation ${creationId} not found`);
      const superseded =
        outcome.acceptedBaseVersion > 0 &&
        (current.activeVersion ?? 0) !== outcome.acceptedBaseVersion;
      const activated = outcome.activate && !superseded;
      const target = await tx.get<CreationVersion>(versionDoc(creationId, outcome.latestVersion));
      if (target?.executionPolicy?.composition || target?.executionPolicy?.compositionV2 || target?.pipelineSnapshot?.id === 'composition-private-v2' || target?.executionPolicy?.pipelineSnapshot?.id === 'composition-private-v2') {
        throw new PlatformError('conflict', 'Composed publication cannot use legacy accepted-lineage activation');
      }
      await new AssetRejectionsRepo(this.store).assertInventoryAllowed(creationId, target?.assetsSnapshot,
        current.assetPolicyRevision ?? 0, tx);
      await new AssetRejectionsRepo(this.store).assertMaterializedAllowed(creationId, target?.gates?.mediaInventory,
        current.assetPolicyRevision ?? 0, tx);
      await new AssetRejectionsRepo(this.store).assertSfxCurrent(creationId, target?.gates?.mediaInventory, tx);
      await tx.update(creationDoc(creationId), {
        buildState: 'idle',
        status: 'live',
        latestVersion: outcome.latestVersion,
        latestBundleUrl: outcome.latestBundleUrl,
        ...(activated
          ? {
              activeVersion: outcome.latestVersion,
              activeBundleUrl: outcome.latestBundleUrl,
              previewUrl: target?.previewUrl ?? null,
              posterUrl: target?.posterUrl ?? null,
            }
          : {}),
        updatedAt: this.store.serverTimestamp(),
      });
      return { activated, superseded };
    });
  }

  /**
   * Return {@link Creation.buildState} to `'idle'` and heal `status` WITHOUT
   * touching the version counters — used when a build is cancelled or reaped
   * (the version + job are failed separately). Like the failed branch of
   * {@link setBuildOutcome} but never writes `latestVersion`/`latestBundleUrl`:
   * a live game whose iteration was cancelled stays `live` (an older version is
   * still playable); a creation that never built lands `failed`. Trusted-writer
   * only. No-op-safe on a missing creation.
   */
  /** Flip {@link Creation.buildState} to 'building' — the platform-initiated
   *  build-enqueue counterpart of {@link clearBuildState} (the art-completion
   *  copy build; creator-initiated builds set it in requestBuild's own write). */
  async markBuilding(creationId: string): Promise<void> {
    await this.store.update(creationDoc(creationId), { buildState: 'building' });
  }

  async clearBuildState(creationId: string): Promise<void> {
    const current = await this.get(creationId);
    if (!current) return;
    const playable = (current.activeVersion ?? 0) > 0 && !!current.activeBundleUrl;
    await this.store.update(creationDoc(creationId), {
      buildState: 'idle',
      status: playable ? 'live' : 'failed',
      updatedAt: this.store.serverTimestamp(),
    });
  }

  /**
   * Stamp the game's QUALITY RATING — the ONE writer of `Creation.rating`.
   *
   * Takes an already-computed rating rather than computing one, because the two
   * inputs live in different places (the audit is on the ACTIVE VERSION's
   * gates, the appeal score is in `editorsPickReviews`) and this repo owns
   * neither. Callers fetch both and run the shared `evaluateRating` engine, so
   * the definition of a rating stays in exactly one file no matter who stamps it.
   *
   * Does NOT bump `updatedAt`: re-rating is the platform measuring a game, not
   * the creator changing one — a rating pass must not resurface the whole
   * catalogue under "Latest" or in trending.
   */
  async setRating(creationId: string, rating: CreationRating): Promise<void> {
    await this.store.update(creationDoc(creationId), { rating });
  }

  /**
   * Stamp when a play last BEGAN on this game — the gallery's "Last played"
   * sort key. Recency ONLY: no counter moves here (the engaged-play funnel owns
   * `playsCount`, and stamps the same field in its own single update), and
   * `updatedAt` is deliberately untouched so a play cannot resurface a game in
   * trending or under "Latest".
   *
   * The caller is the new-play-session edge of a presence beat, which is what
   * makes the sort honest for games launched directly or from an installed PWA
   * — those never touch the portal's Start button, so the engaged-play funnel
   * never sees them. Both writers pass a server clock, so the field advances on
   * its own; there is no ordering guard because two writes of "now" milliseconds
   * apart cannot meaningfully disagree.
   */
  async stampLastPlayed(creationId: string, at: number): Promise<void> {
    await this.store.update(creationDoc(creationId), { lastPlayedAt: at });
  }

  /**
   * Pin the ACTIVE (played) version to a specific live version — rollback or
   * roll-forward. Trusted-writer only (the `setActiveVersion` function validates
   * the version is `live` first). Sets `activeVersion` + `activeBundleUrl`;
   * leaves `latestVersion`/`latestBundleUrl` untouched (the newest build stays
   * the newest — only what's PLAYED changes). Bumps `updatedAt`.
   */
  async setActiveVersion(
    creationId: string,
    activeVersion: number,
    activeBundleUrl: string,
    posterUrl?: string | null,
    previewUrl?: string | null,
  ): Promise<void> {
    const [target, publicPointer] = await Promise.all([
      this.store.getDoc<CreationVersion>(versionDoc(creationId, activeVersion)),
      this.store.getDoc(compositionPublicActiveDocV2(creationId)),
    ]);
    if (publicPointer || target?.executionPolicy?.compositionV2 || target?.pipelineSnapshot?.id === 'composition-private-v2' || target?.executionPolicy?.pipelineSnapshot?.id === 'composition-private-v2') {
      throw new PlatformError('conflict', 'Private composition V2 cannot use generic public activation');
    }
    await this.store.update(creationDoc(creationId), {
      activeVersion,
      activeBundleUrl,
      posterUrl: posterUrl ?? null,
      previewUrl: previewUrl ?? null,
      updatedAt: this.store.serverTimestamp(),
    });
  }

  /**
   * Replace the EDITOR roster and recompute the `memberUids` index in the same
   * write — the two fields must never diverge, so this is the ONLY writer of
   * either. Trusted-writer only (the addEditor/removeEditor functions gate on
   * owner-or-admin and validate the entries); both fields are server-owned in
   * the rules. The creator is never an editor entry; memberUids is always
   * `[creatorUid, ...editor uids]`. Does NOT bump `updatedAt` — roster changes
   * are not content updates (they shouldn't juice trending recency).
   * Throws `PlatformError('not-found')` if the creation is gone.
   */
  async setEditors(creationId: string, editors: EditorEntry[]): Promise<void> {
    const creation = await this.get(creationId);
    if (!creation) {
      throw new PlatformError('not-found', `setEditors: creation ${creationId} not found`);
    }
    const deduped: EditorEntry[] = [];
    const seen = new Set<string>([creation.creatorUid]); // creator never an editor
    for (const e of editors) {
      if (seen.has(e.uid)) continue;
      seen.add(e.uid);
      deduped.push({ uid: e.uid, handle: e.handle });
    }
    await this.store.update(creationDoc(creationId), {
      editors: deduped,
      memberUids: [creation.creatorUid, ...deduped.map((e) => e.uid)],
    });
  }

  /**
   * Attach an approved asset-studio variant to the creation's server-owned
   * `assets` array. Trusted-writer only (the attachAsset function gates on
   * member-or-admin and resolves the variant). Three outcomes:
   *
   * - same `variantId` already attached → IDEMPOTENT no-op (`attached: false`),
   *   so a double-click can't duplicate the entry;
   * - the (case-insensitive) NAME matches an attached asset of the SAME kind →
   *   REPLACE that entry in place (edit-in-place: name = the asset's stable
   *   role; the new image takes over the role, `replaced` carries the old
   *   entry for the caller's audit trail). A kind mismatch throws `conflict` —
   *   a background silently taking over a sprite name would break bindings;
   * - otherwise → append.
   *
   * Every mutation of `assets` (append/replace/detach) changes the ordered
   * variantId sequence, so a CAS precondition pinning that sequence covers
   * every invariant these read-compute-write paths depend on (the repo rule:
   * one CAS covering everything the write was decided from). CAS misses
   * re-read and retry a bounded number of times, then throw `conflict`.
   * Bumps `updatedAt` (an attached asset IS a content update). Throws
   * `not-found` when the creation is gone.
   *
   * `opts.replaceGuard` is the caller's REPLACE policy, evaluated against the
   * freshly-read prior inside the CAS loop (no TOCTOU): when a same-name asset
   * exists and the guard returns false, NOTHING is written and the outcome
   * carries the untouched prior as `preserved`. The builder passes
   * {@link isBuildReplaceable} (a build never clobbers creator-owned art);
   * creator-driven paths (studio/upload) pass no guard — a creator replacing
   * their own asset is always allowed.
   *
   * Cover coupling: a BACKGROUND attached to a cover-less creation becomes the
   * gallery cover (set-once, own CAS — deciding from the pre-read could
   * overwrite a cover committed concurrently by the builder's ensureCoverArt).
   * REPLACING the background whose url IS the current cover moves the cover to
   * the new art in the same set-once-or-follow CAS.
   */
  async attachAsset(
    creationId: string,
    asset: CreationAsset,
    opts?: { replaceGuard?: (prior: CreationAsset) => boolean; workshop?: { originVersion: number; revision: number; actor: string } },
  ): Promise<{ attached: boolean; replaced: CreationAsset | null; preserved?: CreationAsset }> {
    if ((asset.kind === 'music' || asset.kind === 'sfx') && (!asset.audio || !asset.contentSha256)) {
      throw new PlatformError('validation', 'Audio attachments require inspected metadata and exact byte identity');
    }
    // `?? ''` guards legacy attached assets: migration 0011 did not backfill
    // `name` into the creations.assets jsonb, so an asset attached before this
    // rework has no name and `undefined.toLowerCase()` would throw.
    const nameOf = (a: CreationAsset): string => (a.name ?? '').toLowerCase();
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'asset attachment requires transactions');
    return this.store.runTransaction(async tx => {
      const creation = await tx.get<Creation>(creationDoc(creationId));
      if (!creation) {
        throw new PlatformError('not-found', `attachAsset: creation ${creationId} not found`);
      }
      if (opts?.workshop) {
        if (asset.kind !== 'cover') throw new PlatformError('validation', 'Workshop direct attachment is for covers');
        await new WorkshopRepo(this.store).inventory(creationId, opts.workshop.originVersion, opts.workshop.revision, opts.workshop.actor, asset.attachedByHandle, tx);
        const state = (await tx.get<{ state: WorkshopState }>(workshopDoc(creationId)))!.state;
        if (!state.selections.some(s => s.originVersion === opts.workshop!.originVersion && s.variantId === asset.variantId)) {
          throw new PlatformError('conflict', 'Cover selection changed');
        }
      }
      const assets = creation.assets ?? [];
      await new AssetRejectionsRepo(this.store).assertAllowed(creationId, asset.contentSha256, creation.assetPolicyRevision ?? 0, tx);
      if (assets.some((a) => a.variantId === asset.variantId)) {
        return { attached: false, replaced: null };
      }
      const idx = assets.findIndex((a) => nameOf(a) === nameOf(asset));
      const prior = idx === -1 ? null : assets[idx];
      // Replace policy BEFORE the kind check: a guard-refused replacement is a
      // preserved outcome (the caller records provenance), never a conflict.
      // A skip writes nothing, so it needs no CAS of its own.
      if (prior && opts?.replaceGuard && !opts.replaceGuard(prior)) {
        return { attached: false, replaced: null, preserved: prior };
      }
      if (prior && prior.kind !== asset.kind) {
        throw new PlatformError(
          'conflict',
          `attachAsset: "${asset.name}" is attached as a ${prior.kind} — ` +
            `a ${asset.kind} cannot replace it; detach it first or pick another name`,
        );
      }
      const next = assets.slice();
      if (prior && (prior.kind === 'music' || prior.kind === 'sfx') && creation.sfxDraft &&
        [...Object.values(creation.sfxDraft.spec.cues), creation.sfxDraft.spec.music].some(binding => binding && binding !== 'none' && binding.variantId === prior.variantId)) {
        throw new PlatformError('conflict', 'This audio is bound to the working copy. Choose silence before replacing it, or upload under a new name and bind the replacement.');
      }
      if (idx === -1) next.push(asset);
      else next[idx] = asset;
      const compatible = prior?.kind === 'sprite' ? next.filter(a => !a.strip || a.strip.referenceVariantId !== prior.variantId) : next;
      await tx.update(creationDoc(creationId), {
        assets: compatible, updatedAt: this.store.serverTimestamp(),
        ...(asset.kind === 'cover' && (!creation.coverUrl || creation.coverUrl === prior?.url) ? { coverUrl: asset.url } : {}),
      });
      return { attached: true, replaced: prior };
    });
  }

  /**
   * Replace a gallery cover produced by the platform's managed refresh queue.
   *
   * The refresh is planned against a particular `coverUrl`; generation can
   * take several minutes, during which its creator may choose a new cover in
   * Studio. This one CAS protects BOTH the assets list and `coverUrl`, so a
   * stale refresh is a harmless no-op instead of clobbering that choice.
   */
  async replaceCoverFromGenerated(
    creationId: string,
    expectedCoverUrl: string | null,
    asset: CreationAsset,
  ): Promise<{ attached: boolean; replaced: CreationAsset | null; stale: boolean }> {
    if (asset.kind !== 'cover' || asset.name.toLowerCase() !== 'cover') {
      throw new PlatformError('validation', 'replaceCoverFromGenerated requires the cover asset named "cover"');
    }
    const nameOf = (a: CreationAsset): string => (a.name ?? '').toLowerCase();
    for (let attempt = 0; attempt < ASSET_CAS_RETRIES; attempt++) {
      const creation = await this.get(creationId);
      if (!creation) throw new PlatformError('not-found', `replaceCoverFromGenerated: creation ${creationId} not found`);
      if ((creation.coverUrl ?? null) !== expectedCoverUrl) return { attached: false, replaced: null, stale: true };
      await new AssetRejectionsRepo(this.store).assertAllowed(creationId, asset.contentSha256, creation.assetPolicyRevision ?? 0);

      const assets = creation.assets ?? [];
      const idx = assets.findIndex((candidate) => nameOf(candidate) === 'cover');
      const prior = idx === -1 ? null : assets[idx];
      if (prior && prior.kind !== 'cover') {
        throw new PlatformError('conflict', 'replaceCoverFromGenerated: "cover" is attached as a non-cover asset');
      }
      if (prior?.variantId === asset.variantId) return { attached: false, replaced: null, stale: false };

      const next = assets.slice();
      if (idx === -1) next.push(asset);
      else next[idx] = asset;
      const sig = assetSig(assets);
      const committed = await this.store.guardedUpdate<{ assets?: CreationAsset[]; coverUrl?: string; assetPolicyRevision?: number }>(
        creationDoc(creationId),
        (current) =>
          current !== null &&
          assetSig(current.assets ?? []) === sig &&
          (current.assetPolicyRevision ?? 0) === (creation.assetPolicyRevision ?? 0) &&
          (current.coverUrl ?? null) === expectedCoverUrl,
        { assets: next, coverUrl: asset.url, updatedAt: this.store.serverTimestamp() },
      );
      if (committed) return { attached: true, replaced: prior, stale: false };
    }
    throw new PlatformError('conflict', `replaceCoverFromGenerated: assets changed concurrently ${ASSET_CAS_RETRIES} times`);
  }

  /**
   * Detach an attached asset by its (case-insensitive) NAME — the inverse of
   * {@link attachAsset}. Trusted-writer only (the detachAsset function gates on
   * member-or-admin). Returns the removed entry, or null when no attached
   * asset carries the name (idempotent). Same ordered-variantId CAS + bounded
   * retry as attach. Bumps `updatedAt`. Never touches `coverUrl` — the cover
   * is a display artifact owned by the set-once stamp + the builder's
   * publish-time ensureCoverArt. Throws `not-found` when the creation is gone.
   */
  async detachAsset(creationId: string, name: string): Promise<CreationAsset | null> {
    const needle = name.toLowerCase();
    for (let attempt = 0; attempt < ASSET_CAS_RETRIES; attempt++) {
      const creation = await this.get(creationId);
      if (!creation) {
        throw new PlatformError('not-found', `detachAsset: creation ${creationId} not found`);
      }
      const assets = creation.assets ?? [];
      const idx = assets.findIndex((a) => (a.name ?? '').toLowerCase() === needle);
      if (idx === -1) return null;
      const removed = assets[idx];
      if ((removed.kind === 'music' || removed.kind === 'sfx') && creation.sfxDraft &&
        [...Object.values(creation.sfxDraft.spec.cues), creation.sfxDraft.spec.music].some(binding => binding && binding !== 'none' && binding.variantId === removed.variantId)) {
        throw new PlatformError('conflict', 'This audio is bound to the working copy. Choose silence or another sound before detaching it.');
      }
      const sig = assetSig(assets);
      const committed = await this.store.guardedUpdate<{ assets?: CreationAsset[]; assetPolicyRevision?: number }>(
        creationDoc(creationId),
        (current) => current !== null && assetSig(current.assets ?? []) === sig &&
          (current.assetPolicyRevision ?? 0) === (creation.assetPolicyRevision ?? 0),
        {
          assets: assets.filter((_, i) => i !== idx),
          updatedAt: this.store.serverTimestamp(),
        },
      );
      if (committed) return removed;
    }
    throw new PlatformError(
      'conflict',
      `detachAsset: assets changed concurrently ${ASSET_CAS_RETRIES} times — retry`,
    );
  }

  /**
   * The subcollections a creation owns. Deleting a creation must clear ALL of
   * them — listed here, in the repo, so a new child collection is added in one
   * place rather than remembered at the call site.
   */
  private static readonly SUBCOLLECTIONS = [
    versionsCollection,
    likesCollection,
    viewShardsCollection,
    viewersCollection,
  ] as const;

  /**
   * Delete a creation, its subcollections and its slug claim — ATOMICALLY, in
   * one batch. No-op-safe if the creation is already gone (the slug claim, if
   * any, is still released).
   *
   * The cascade used to live in the deleteCreation handler as a nested
   * read-then-delete loop that ran BEFORE a separate `delete()`, so a crash
   * mid-sweep left orphaned versions/likes/viewers behind a live creation, and a
   * crash between sweep and delete left a creation with no versions. Both are
   * unreachable now: the batch either lands whole or not at all.
   *
   * Returns how many subdocs went with it (the handler reports this in the
   * audit-log entry).
   */
  async delete(creationId: string): Promise<{ deletedSubdocs: number }> {
    const creation = await this.get(creationId);
    const ops: Parameters<DocStorePort['batch']>[0] = [];

    let deletedSubdocs = 0;
    for (const collectionOf of CreationsRepo.SUBCOLLECTIONS) {
      const path = collectionOf(creationId);
      for (const doc of await this.store.query<{ id: string }>(path)) {
        ops.push({ type: 'delete', path: `${path}/${doc.id}` });
        deletedSubdocs++;
      }
    }

    ops.push({ type: 'delete', path: creationDoc(creationId) });
    if (creation) ops.push({ type: 'delete', path: slugDoc(creation.slug) });

    await this.store.batch(ops);
    return { deletedSubdocs };
  }

  /** True once an intent proposal exists for this creation. Those rows are
   *  retained on purpose and their foreign keys block deleting the creation. */
  async hasRetainedIntentProposal(creationId: string): Promise<boolean> {
    return (await this.store.getDoc(compositionIntentProposalHeadDocV2(creationId))) !== null;
  }

  /**
   * {@link delete} for a creator's own draft: the same cascade, but only while
   * the creation is still an unbuilt draft. The check and the delete share one
   * transaction holding the creation lock that build acceptance also takes, so a
   * build accepted concurrently either lands first and refuses this delete, or
   * finds the creation gone. Throws `permission` for a built game.
   */
  async deleteUnbuiltDraft(creationId: string): Promise<{ deletedSubdocs: number }> {
    if (!this.store.runTransaction) throw new PlatformError('unavailable', 'draft delete requires server transactions');
    return this.store.runTransaction(async (tx) => {
      const creation = await tx.get<Creation>(creationDoc(creationId));
      if (!creation) return { deletedSubdocs: 0 };
      if (!isUnbuiltDraft(creation)) {
        throw new PlatformError('permission', 'only an unbuilt draft can be deleted by its creator');
      }
      if (!tx.query) throw new PlatformError('unavailable', 'draft delete requires transactional queries');
      let deletedSubdocs = 0;
      for (const collectionOf of CreationsRepo.SUBCOLLECTIONS) {
        // View shards are virtual on Postgres (creations.views_count), cannot be
        // queried in a transaction, and go with the creation row.
        if (collectionOf === viewShardsCollection) continue;
        const path = collectionOf(creationId);
        for (const doc of await tx.query<{ id: string }>(path)) {
          await tx.delete(`${path}/${doc.id}`);
          deletedSubdocs++;
        }
      }
      await tx.delete(creationDoc(creationId));
      await tx.delete(slugDoc(creation.slug));
      return { deletedSubdocs };
    });
  }
}
